// usePointerMode などからの「ユーザー操作」を CanvasOp へ変換し、
// 楽観更新 (applyOptimistic) → realtime クライアント送信を一括で行う dispatcher。

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useShowToast } from "./notice/use-show-toast";
import {
  TEXT_UPDATE_MESSAGE_MAX_BYTES,
  textLengthClampNotice,
  textNotSentNotice,
} from "./input/text-body-send-fit";
import {
  CANVAS_OBJECT_HARD_LIMIT,
  CANVAS_OBJECT_SOFT_LIMIT,
  CANVAS_TRASHED_EDIT_MESSAGE,
  TRANSIENT_TRANSFORM_MANY_MAX_ITEMS,
  clampCanvasObjectToCanvas,
  clampPointToCanvas,
  computePenStrokeFrame,
  normalizeCanvasSizePreset,
  COMMENT_MESSAGES_MAX,
  type CanvasOp,
  type CanvasObject,
  type CanvasSizePreset,
  type ClientMessage,
  type CommentMessage,
  type PenStrokeCanvasObject,
  type StickyCanvasObject,
  type TextCanvasObject,
  type ShapeCanvasObject,
  type StrokePattern,
  type ImageCanvasObject,
  type VideoCanvasObject,
  type LinkCardCanvasObject,
  type ConnectorEndpoint,
  type ConnectorFrame,
  connectorStoredFrame,
  collectConnectorCascadeDeleteIds,
  isConnectorEndpointObject,
  SECTION_DEFAULT_FILL,
  SHAPE_STROKE_WIDTH_MAX_PX,
  clampZOrderForPersist,
  applyTextStyleUpdate,
  clampTextBodyAtCaret,
  isSameTextInlineStyle,
  rebaseTextInlineStyles,
  textBaseStyleOf,
  textStyleRevisionOf,
  TEXT_BODY_MAX_LENGTH,
  TEXT_INLINE_STYLE_KEYS,
  type TextBaseStylePatch,
  type TextInlineRangePatch,
  type TextInlineStyle,
  type TextInlineStyleKey,
  type TextInlineStylePatch,
  type TextInlineStyleRange,
  type TransformObjectOp,
  fitTableCellsForBudget,
  normalizeTableCells,
  applyTableCellInlineRangePatches,
  applyTableCellUpdate,
  applyTableColumnWidthsUpdate,
  applyTableShapeUpdate,
  cellInlineStylesAt,
  tableColumnWidthsAt,
  type TableColumnWidths,
  type UpdateTableColumnWidthsOp,
  deleteTableColumnFraction,
  insertTableColumnFraction,
  isValidTableColumnFractions,
  tableStructureRevisionOf,
  TABLE_CELLS_MAX_JSON_BYTES,
  TABLE_CELL_MAX_LENGTH,
  type TableCanvasObject,
  type TableShapeChange,
  type TableShapeChangeInlineStyles,
  type UpdateObjectStyleOp,
  type UpdateTableCellOp,
  type UpdateTableShapeOp,
  type UpdateTextStyleOp,
} from "@refinement-canvas/domain";
import { generateCanvasOpId as generateId } from "../../lib/canvas-op-id";
import { normalizeColorForCompare } from "../../lib/pen-preference";
import {
  normalizeShapeFillForCompare,
  normalizeShapeStrokeForCompare,
  SHAPE_DEFAULT_STROKE,
  SHAPE_DEFAULT_STROKE_WIDTH,
  SHAPE_FILL_TRANSPARENT,
} from "../../lib/shape-preference";
import { STICKY_DEFAULT_COLOR } from "../../lib/sticky-preference";
import { DEFAULT_TEXT_FONT_SIZE } from "../../lib/text-size-preference";
import {
  CONNECTOR_DEFAULT_STROKE,
  CONNECTOR_DEFAULT_STROKE_WIDTH,
  normalizeConnectorStrokeForCompare,
} from "../../lib/connector-style";
import {
  normalizeTextFillForCompare,
  TEXT_DEFAULT_COLOR,
  TEXT_FILL_TRANSPARENT,
} from "../../lib/text-color";
import {
  normalizePenStrokePoints,
  roundPenStrokePoints,
  utf8ByteLength,
  PEN_STROKE_FIT_MAX_ATTEMPTS,
  PEN_STROKE_MESSAGE_MAX_BYTES,
  PEN_STROKE_MIN_POINTS_BUDGET_BYTES,
} from "../../lib/pen-stroke-payload";
import type { StrokePoint } from "../../lib/pen-stroke";
import { resolveStrokePattern } from "../../lib/stroke-pattern";
import { CLIENT_MESSAGE_MAX_BYTES } from "../../realtime/websocket-realtime";

// コメントスレッドオブジェクトの保存上の名目サイズ（ステージ px）。
// ピンはビューポート固定サイズのオーバーレイ層で描画するため、x/y をアンカー（クリック位置）とし、
// w/h は範囲内 clamp 用の最小フットプリントとしてのみ使う。
export const COMMENT_THREAD_NOMINAL_SIZE = 1;
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasStore } from "./store/canvas-store";
import { useRealtimeClient } from "../../realtime/realtime-context";
import {
  clearTableOpQueue,
  configureTableOpQueue,
  enqueueTableOp,
  type TableQueuedOp,
} from "./table-op-queue";

interface DispatcherContext {
  readonly actorUserId: string;
  /**
   * 操作者の表示名（日本語名）。op.actorDisplayName へ転写し、サーバが付箋の本文が変わる
   * op のときだけ sticky の updatedByName へ反映するための情報源。未指定なら省略。
   */
  readonly actorDisplayName?: string | undefined;
  /**
   * 操作者のアバター URL（コメント投稿時の楽観表示兼、接続時アバター欠落時の永続化候補）。
   * サーバは接続時アバターを優先し、欠落時だけ安全な自己完結ラスタ data URL と検証して採用する。
   */
  readonly actorAvatarImageUrl?: string | undefined;
  /**
   * 操作者が管理者権限を持つか。コメント編集・削除のクライアント側ガードに使う
   * （ボタン表示の最終判定は UI 側、書き込みの最終判定はサーバ側）。
   */
  readonly actorCanAdmin?: boolean | undefined;
  /** 表の作成時に送信上限へ収めるためセル本文を切り詰めたことを通知する。 */
  readonly onTableCellsClamped?: ((objectId: string) => void) | undefined;
}

// CanvasObject は discriminated union のため、Omit を distributive に展開して
// 個別 type の draft 入力（text / shapeKind / src 等）を保持できるようにする。
type DistributiveDraft<T> = T extends CanvasObject
  ? Omit<T, "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"> & {
      readonly id?: string | undefined;
    }
  : never;

export type CanvasObjectDraft = DistributiveDraft<CanvasObject>;

export interface CanvasOpDispatcher {
  readonly createObject: (object: CanvasObjectDraft) => CanvasObject | null;
  /** 本文を空にした object.create メッセージの UTF-8 バイト数を実測する。 */
  readonly measureCreateEnvelopeBytes: (object: CanvasObjectDraft) => number;
  readonly createObjects: (objects: readonly CanvasObjectDraft[]) => readonly CanvasObject[];
  /**
   * 図形と図形を結ぶコネクタ（接続矢印）を作成する。
   * 端点（from / to）の現在位置からルートを算出し、その外接矩形を保存用 x/y/w/h に用いる。
   * 端点オブジェクトが存在しない場合や自己ループの場合は作成せず null を返す。
   */
  readonly createConnector: (
    from: ConnectorEndpoint,
    to: ConnectorEndpoint,
    stroke?: string | undefined
  ) => CanvasObject | null;
  /**
   * 画像・動画の添付を「先に仮表示、あとから本体同期」で配置する。
   *
   * - `object.src` には軽量なプレースホルダ（画像なら超圧縮 LQIP の data URL、
   *   動画なら 1x1 透明 GIF など）を入れて全参加者へ create を即配信する。
   *   blob: URL は他参加者が解決できないため、ここには絶対に入れない。
   * - 作成者本人だけは即フル画質を見られるよう、送信しないローカル専用の
   *   attachment.update（src=localFullSrc=blob URL）を重ねて適用する。
   *
   * 戻り値の overrideOpId は、本体アップロード完了後に
   * {@link finalizeAttachmentPlaceholder} / {@link discardAttachmentPlaceholder} へ渡す。
   */
  readonly createAttachmentPlaceholder: (
    object: CanvasObjectDraft,
    localFullSrc: string
  ) => { readonly object: CanvasObject; readonly overrideOpId: string } | null;
  /**
   * アップロード完了時に呼ぶ。公開 URL を全参加者へ attachment.update で配信し、
   * 本人用ローカル上書き（blob プレビュー）を破棄して全員を公開 URL 表示へ収束させる。
   */
  readonly finalizeAttachmentPlaceholder: (
    objectId: string,
    overrideOpId: string,
    publicSrc: string
  ) => void;
  /**
   * アップロード失敗時に呼ぶ。本人用ローカル上書きを破棄し、仮表示オブジェクトを
   * 全参加者から削除する（壊れた状態を残さない）。
   */
  readonly discardAttachmentPlaceholder: (objectId: string, overrideOpId: string) => void;
  readonly transformObject: (objectId: string, transform: ObjectTransformPatch) => void;
  /**
   * 本文・セルの編集に伴い、図形枠の大きさ（w / h）を実描画の大きさへ自動で追随させる。
   * `frame` に渡した辺だけが追随し、省略した辺は変えない（テキスト図形は高さだけ、
   * 表図形は幅と高さを 1 回の呼び出しで渡す）。
   * ユーザーの明示操作ではない派生的なレイアウト調整のため、Undo 履歴には積まない
   * （入力中の Ctrl+Z は本文編集の取り消しに充てる）。
   *
   * 縮小（w / h を小さくする）方向は `allowShrink: true` を明示したときだけ反映する。
   * 同時編集では「未送信のローカル変更がある間は各クライアントが自分の本文を保持する」ため、
   * 入力中のライブ実測で縮小まで許すと、短い本文を保持しているクライアントが他者の長い本文の
   * 高さを縮め戻してしまう（last-writer-wins の打ち合いで選択枠やコネクタが暴れる）。
   * よって呼び出し側は、実測が権威本文と対応する場面（編集開始時・編集確定時）だけ
   * allowShrink を立て、入力中のライブ追随は拡張のみとする。
   */
  readonly autoFitObjectFrame: (
    objectId: string,
    frame: { readonly w?: number | undefined; readonly h?: number | undefined },
    options?: { readonly allowShrink?: boolean | undefined } | undefined
  ) => void;
  /**
   * 複数オブジェクトの transform を **1 回の履歴エントリ** にまとめて適用する。
   * グループドラッグなど「ユーザーから見れば 1 操作」を 1 回の Undo で戻せるようにする。
   */
  readonly transformObjects: (
    updates: readonly { readonly objectId: string; readonly transform: ObjectTransformPatch }[]
  ) => void;
  /**
   * 本文を更新する。テキスト図形では、本文変更に追従させた部分書式（`inlineStyles`）と
   * 編集元 `textStyleRevision` を同時に送れる（サーバは revision 不一致を競合 reject する）。
   * 付箋・セクション名など非 text 対象では options を渡してはいけない（サーバが拒否する）。
   */
  readonly updateText: (
    objectId: string,
    text: string,
    options?:
      | {
          readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
          readonly expectedTextStyleRevision?: number | undefined;
        }
      | undefined
  ) => void;
  /**
   * 複数オブジェクトの本文を **1 回の履歴エントリ** にまとめて更新する。
   * 存在しない / ロック済み / 変化の無い対象は飛ばし、効果のある op が 1 件も無ければ何も送らない。
   * テキスト図形では本文へ追従させた部分書式（inlineStyles）も同じ op で確定する。
   *
   * この経路には編集器の `fitTextBodyForSend` が無いため、本文長に加えて**送信サイズ上限も
   * ここで実測**し、超える対象は送信・履歴追加の前に除外して 1 回だけ通知する。
   */
  readonly updateTexts: (
    updates: readonly {
      readonly objectId: string;
      readonly text: string;
      readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
    }[]
  ) => void;
  /**
   * その本文・部分書式で object.text.update を送った場合の、送信メッセージ（op.apply）の
   * UTF-8 バイト数を実測する。送信サイズ上限へ収める事前調整だけに使い、op は送らない。
   * clientOpId / clientCreatedAt は長さが一定の値を使うため、実際の送信との差は数バイトに収まる。
   */
  readonly measureTextUpdateMessageBytes: (
    objectId: string,
    text: string,
    inlineStyles?: readonly TextInlineStyleRange[] | undefined
  ) => number;
  /**
   * テキスト図形の文字範囲へ書式を 1 項目だけ適用する（範囲専用パネル）。
   * 逆 op には「対象範囲の当該項目を消す patch」と「変更前 override を区間ごとに戻す patch 列」を
   * 1 つの op へ格納し、1 履歴項目・1 サーバ commit で完全復元する。
   *
   * `range.text` には「その `start` / `end` が指していた本文」を渡す。選択してから操作するまでの
   * あいだに他参加者が本文を変えていた場合、保存本文と照合して送信を止める（同じ offset を
   * 新しい本文へ当てると、別の文字へ書式が付く）。
   * 適用できない場合（対象不在・ロック・本文不一致・run 上限超過）は送信せず false を返す。
   */
  readonly updateTextRangeStyle: (
    objectId: string,
    range: { readonly start: number; readonly end: number; readonly text: string },
    patch: TextInlineStylePatch
  ) => boolean;
  /**
   * 色・フォントサイズ・文字寄せ等の見た目だけを変更する。位置・サイズは transformObject 側を使う。
   * style の形は op 型を正本にして再宣言しない（キー追加時の追従漏れを型エラーで検出するため）。
   */
  readonly updateObjectStyle: (objectId: string, style: UpdateObjectStyleOp["style"]) => void;
  /**
   * 複数オブジェクトの見た目を **1 回の履歴エントリ** にまとめて変更する。
   * 存在しない / ロック済みの対象は飛ばし、効果のある op が 1 件も無ければ何も送らない。
   */
  readonly updateObjectsStyle: (
    objectIds: readonly string[],
    style: UpdateObjectStyleOp["style"]
  ) => void;
  /**
   * 表のセル 1 つの本文を更新する。
   * 対象不在・非表・ロック中・範囲外・上限超過では送信せず false を返す。
   * 送信は表ごとに直列化され、同じセルへの連続入力は未送信のあいだだけ最新値へ畳まれる。
   *
   * `structureRevision` には「その row / column を決めた時点の構造 revision」を渡す。
   * 省略すると現在値を使うため、行・列の増減を挟んだ古い位置の入力が**別のセル**へ
   * 当たり得る（増減後も添字は範囲内のまま意味だけが変わるため、範囲検査では防げない）。
   */
  readonly updateTableCell: (
    objectId: string,
    row: number,
    column: number,
    text: string,
    structureRevision?: number | undefined
  ) => boolean;
  /**
   * 表のセルの一部（または全体）へ書式を適用する。
   *
   * 現在の run と本文から新しい run を組み立て、**本文と同じ op**（`object.table.cell.update`）で
   * 送る。本文は変えず、run だけを差し替える。逆 op には変更前の run を載せ、1 履歴項目で戻せる
   * ようにする。
   *
   * `position.structureRevision` には「その row / column を決めた時点の構造 revision」を渡す。
   * 省略できないのは、現在値で組み直すと他参加者の行・列増減を挟んだときに**別のセル**へ書式が
   * 当たるためである（`updateTableCell` と同じ理由）。
   *
   * `range` は保存済み本文の長さで切り詰めてから適用する（他参加者が本文を短くしていた場合に、
   * 範囲外へ run を作らないため）。切り詰めた結果が空区間なら送らない。
   * 適用できない場合（対象不在・ロック・構造の不一致・上限超過・変化なし）は送信せず false を返す。
   */
  readonly updateTableCellRangeStyle: (
    objectId: string,
    position: {
      readonly row: number;
      readonly column: number;
      readonly structureRevision: number;
    },
    range: { readonly start: number; readonly end: number },
    patch: TextInlineStylePatch
  ) => boolean;
  /**
   * 表の行・列を 1 つ増減する。削除の逆 op は「内容つきの挿入」1 件で表し、
   * op の到着順に依存せず本文ごと復元できるようにする。
   * 適用できない場合（対象不在・ロック・上限 / 下限）は送信せず false を返す。
   */
  readonly updateTableShape: (objectId: string, change: TableShapeChange) => boolean;
  /**
   * テキスト図形・付箋の本文中にある表 1 つぶんの列幅を更新する。
   * `widthsEm` が空配列のときは「保存を破棄して自動列幅へ戻す」を表す。
   *
   * `tableCount` には**その本文から解析した表の総数**を渡す。サーバはこれを超える保存値を
   * 切り捨てるため、本文編集で表が減ったときの余剰値がここで掃除される。
   * `headerKey` には解析した表のヘッダ指紋（`markdownTableHeaderKey`）を渡す。
   *
   * 逆 op には「変更前の同じ index の値」を載せ、1 履歴項目で戻せるようにする。
   * 適用できない場合（対象不在・非対応種別・ロック・範囲外・変化なし）は送信せず false を返す。
   */
  readonly updateTableColumnWidths: (
    objectId: string,
    tableIndex: number,
    widthsEm: readonly number[],
    tableCount: number,
    headerKey: string
  ) => boolean;
  /**
   * 対象の表の**未送信** op を破棄する（送信済みで応答待ちの op はサーバの確定・拒否に委ねる）。
   * 表の削除・ロック・参照専用への遷移、および他参加者の行・列増減で編集を打ち切るときに使う。
   * 破棄した op の楽観適用も取り消すため、送られない変更が「保存済み」の見た目で残らない。
   */
  readonly discardTableOps: (objectId: string) => void;
  /**
   * 1 回のセル滞在ぶんの差分を、キャンバス履歴へ 1 グループとして記録する（op は送らない）。
   * 入力中の op は `updateTableCell` が debounce で送るため、履歴だけをここで確定させる。
   * 差分がゼロなら項目を残さない。
   *
   * `baseInlineStyles` には**編集を開始した時点のそのセルの run** を渡す。確定後の run から
   * 逆算すると、書式が付いていた文字ごと消した編集で run が空になり、Undo で書式を戻せない
   * （空の run からは `rebaseTextInlineStyles` が書式を作り直せないため）。
   * 省略は「編集開始時点でそのセルに部分書式が無かった」を表す。
   *
   * 戻り値は「履歴項目を積んだか」。呼び出し側が、自分の操作順と**キャンバス履歴の実際の並び**を
   * 対応づける（積まれていない項目を取り消そうとしない）ために使う。
   */
  readonly recordTableCellHistory: (
    objectId: string,
    row: number,
    column: number,
    baseText: string,
    finalText: string,
    baseInlineStyles?: readonly TextInlineStyleRange[] | undefined
  ) => boolean;
  readonly updateAttachmentSrc: (objectId: string, src: string) => void;
  readonly updateCanvasMeta: (input: {
    title?: string | undefined;
    thumbnailKey?: string | undefined;
    sizePreset?: CanvasSizePreset | undefined;
  }) => void;
  readonly deleteObject: (objectId: string) => void;
  readonly deleteObjects: (objectIds: readonly string[]) => void;
  /**
   * コメントスレッドを新規作成する（起点コメント 1 件を含む comment-thread オブジェクトを配置）。
   * x / y はピンのアンカー（クリック位置・ステージ座標）。images は添付画像の公開 URL（任意）。
   * 本文が空でかつ画像も無いときは作成しない（null を返す）。
   */
  readonly createCommentThread: (input: {
    x: number;
    y: number;
    body: string;
    images?: readonly string[] | undefined;
  }) => CanvasObject | null;
  /** 既存スレッドへ返信メッセージを追加する。本文と画像の両方が空・上限超過時は何もしない。 */
  readonly addCommentMessage: (
    objectId: string,
    body: string,
    images?: readonly string[] | undefined
  ) => void;
  /** スレッド内の 1 メッセージ本文を編集する（投稿者本人 / 管理者のみ。最終判定はサーバ）。 */
  readonly updateCommentMessage: (objectId: string, messageId: string, body: string) => void;
  /** スレッド内の 1 メッセージを削除する（投稿者本人 / 管理者のみ。最終判定はサーバ）。 */
  readonly deleteCommentMessage: (objectId: string, messageId: string) => void;
  readonly undo: () => void;
  readonly redo: () => void;
  readonly commitPenStroke: (
    points: ReadonlyArray<{ x: number; y: number; pressure: number }>,
    color: string,
    thickness: number,
    boundingBox: { x: number; y: number; w: number; h: number },
    /** 描画中 transient と同一の strokeId。確定オブジェクト ID に採用してゴースト破棄を正確化する。 */
    objectId?: string,
    strokePattern?: StrokePattern
  ) => PenStrokeCanvasObject | null;
  /**
   * 操作中（未確定）の transform を他参加者へ一時 op として中継する。
   * ゴースト表示用であり永続化しない。ドラッグ移動 / リサイズ / 回転の途中経過に使う。
   */
  readonly previewTransforms: (
    updates: readonly { readonly objectId: string; readonly transform: ObjectTransformPatch }[]
  ) => void;
  /** 描画中ペンストロークの増分点を他参加者へ一時 op として中継する（ゴースト表示用）。 */
  readonly previewPenAppend: (
    strokeId: string,
    points: ReadonlyArray<{ x: number; y: number; pressure: number }>,
    strokePattern?: StrokePattern
  ) => void;
  /**
   * テキスト / 付箋を編集中のキャレット位置（本文先頭からの文字オフセット）を他参加者へ
   * 一時 op として中継する（他者のカーソル表示用、永続化しない）。
   *
   * selectionEndIndex は範囲選択中の終端境界オフセット。範囲は半開区間
   * [cursorIndex, selectionEndIndex) で、丸めた結果が cursorIndex 以下になる場合と
   * 省略された場合はフィールド自体を載せない（受信側は「選択なし」として扱う）。
   */
  readonly previewTextCursor: (
    objectId: string,
    cursorIndex: number,
    selectionEndIndex?: number | undefined
  ) => void;
  /**
   * 表のセルを編集中のキャレット位置を他参加者へ一時 op として中継する。
   * `cursorIndex` はそのセルの本文内オフセット。セル編集では選択範囲を共有しない（§21.2）。
   */
  readonly previewTableCellCursor: (
    objectId: string,
    cell: { readonly row: number; readonly column: number },
    cursorIndex: number
  ) => void;
}

export type ObjectTransformPatch = Partial<{
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  zOrder: number;
  locked: boolean;
  manualHeight: boolean;
}>;

interface HistoryEntry {
  readonly undo: readonly CanvasOp[];
  readonly redo: readonly CanvasOp[];
}

// op に共通して付与するメタ情報（makeMeta の戻り値）。全 op が BaseOp として持つ。
type OpMeta = Pick<CanvasOp, "clientOpId" | "clientCreatedAt" | "actorUserId" | "actorDisplayName">;

const undoStack: HistoryEntry[] = [];
const redoStack: HistoryEntry[] = [];
const HISTORY_LIMIT = 100;

export function clearCanvasHistory(): void {
  undoStack.length = 0;
  redoStack.length = 0;
}

export function useCanvasOpDispatcher(ctx: DispatcherContext): CanvasOpDispatcher {
  const applyOptimistic = useCanvasDocStore((s) => s.applyOptimistic);
  const applyOptimisticMany = useCanvasDocStore((s) => s.applyOptimisticMany);
  const removeInFlightOp = useCanvasDocStore((s) => s.removeInFlightOp);
  const realtime = useRealtimeClient();
  const showToast = useShowToast();
  const isTrashed = useCanvasDocStore((s) => s.meta.isTrashed ?? false);
  const lastSoftLimitToastAtRef = useRef(0);

  const rejectEditWhenTrashed = useCallback((): boolean => {
    if (!isTrashed) return false;
    showToast("warning", CANVAS_TRASHED_EDIT_MESSAGE);
    return true;
  }, [isTrashed, showToast]);

  /**
   * op を送る。戻り値は「この op が送信経路に受け付けられたか」。
   *
   * 表の op は送信キューが受け付けを判断するため、待機上限に達すると受け付けられない。
   * 呼び出し側はこの結果を見て、**送られなかった op の履歴を積まない**
   * （積むと、実行されなかった操作の逆 op だけが後の Ctrl+Z で送られ、既存の行・列を壊す）。
   */
  const sendOp = useCallback(
    (op: CanvasOp): boolean => {
      // 画面は即座に更新する（楽観適用は待たない）。表の op だけ、送信の順序をキューが決める。
      applyOptimistic(op);
      if (isTableQueuedOp(op)) {
        // 待機上限に達して受け付けられなかった場合は、送られない op を in-flight に残さない。
        if (!enqueueTableOp(op)) {
          removeInFlightOp(op.clientOpId);
          return false;
        }
        return true;
      }
      realtime.sendOp(op);
      return true;
    },
    [applyOptimistic, realtime, removeInFlightOp]
  );

  // 表の送信キューへ、送信・楽観適用の取り消し・通知・再同期の依頼先を渡す。
  // canvas の切替・破棄ではキューを解放し、別 canvas へ送らない。
  useEffect(() => {
    configureTableOpQueue({
      send: (op) => realtime.sendOp(op),
      dropOptimistic: (clientOpId) => removeInFlightOp(clientOpId),
      notify: (message) => showToast("warning", message),
      // 応答が届かない場合は欠番バッファが空のままのため、欠番専用の requestGapResync では
      // 何も起きない。欠番の有無によらない権威スナップショット再取得へ接続する。
      requestResync: () => useCanvasDocStore.getState().requestSnapshotResync(),
    });
    return () => configureTableOpQueue(null);
  }, [realtime, removeInFlightOp, showToast]);

  /**
   * op を送り、受け付けられた場合だけ履歴を積む。戻り値は受け付けの成否。
   *
   * 履歴を先に積むと、送信キューが受け付けなかった操作の逆 op が Ctrl+Z で送られてしまう
   * （実行されていない行挿入の逆＝行削除が、既存の行を消す）。
   */
  const dispatch = useCallback(
    (op: CanvasOp, history?: HistoryEntry | undefined): boolean => {
      if (!sendOp(op)) return false;
      if (history) {
        undoStack.push(history);
        if (undoStack.length > HISTORY_LIMIT) undoStack.shift();
        redoStack.length = 0;
      }
      return true;
    },
    [sendOp]
  );

  const dispatchMany = useCallback(
    (ops: readonly CanvasOp[], history?: HistoryEntry | undefined): void => {
      if (ops.length === 0) return;
      if (history) {
        undoStack.push(history);
        if (undoStack.length > HISTORY_LIMIT) undoStack.shift();
        redoStack.length = 0;
      }
      applyOptimisticMany(ops);
      for (const op of ops) realtime.sendOp(op);
    },
    [applyOptimisticMany, realtime]
  );

  const makeMeta = useCallback(
    () => ({
      clientOpId: generateId(),
      clientCreatedAt: new Date().toISOString(),
      actorUserId: ctx.actorUserId,
      // displayName が無い場合はキーごと送らず、旧来 op 形式と差分が出ないようにする。
      ...(ctx.actorDisplayName ? { actorDisplayName: ctx.actorDisplayName } : {}),
    }),
    [ctx.actorUserId, ctx.actorDisplayName]
  );

  const canCreateObjects = useCallback(
    (addCount: number): boolean => {
      const currentCount = Object.keys(useCanvasDocStore.getState().objects).length;
      const nextCount = currentCount + addCount;
      if (nextCount > CANVAS_OBJECT_HARD_LIMIT) {
        showToast(
          "error",
          `このキャンバスはオブジェクト数の上限（${CANVAS_OBJECT_HARD_LIMIT.toLocaleString()}件）に達しています。新しいキャンバスへ分割して作業を続けてください。`
        );
        return false;
      }

      if (nextCount >= CANVAS_OBJECT_SOFT_LIMIT) {
        const now = Date.now();
        if (now - lastSoftLimitToastAtRef.current > 30_000) {
          lastSoftLimitToastAtRef.current = now;
          showToast(
            "warning",
            `このキャンバスのオブジェクト数が多くなっています（${nextCount.toLocaleString()}件）。動作が重い場合は、新しいキャンバスへの分割をご検討ください。`
          );
        }
      }
      return true;
    },
    [showToast]
  );

  // create 用の object と op を組み立てる共通処理。上限チェックを行い、
  // 通過しなければ null を返す（即時 create と「仮表示プレースホルダ」create で共有する）。
  // 当初仕様（キャンバス外にも配置可）へ戻したため、キャンバス範囲外チェックは行わない。
  const buildCreateOp = useCallback(
    (input: CanvasObjectDraft): { object: CanvasObject; op: CanvasOp } | null => {
      if (rejectEditWhenTrashed()) return null;
      if (!canCreateObjects(1)) return null;
      const id = input.id ?? generateId();
      const now = new Date().toISOString();
      const objects = useCanvasDocStore.getState().objects;
      const nextZ = computeNextZOrder(objects);
      const baseObject = clampCanvasObjectToCanvas({
        ...input,
        id,
        version: 1,
        zOrder: nextZ,
        createdAt: now,
        createdBy: ctx.actorUserId,
        updatedAt: now,
        updatedBy: ctx.actorUserId,
      } as CanvasObject);
      // 付箋の初期作成時にも、最終更新者名スナップショットを埋め込む（後続編集と同じ挙動にする）。
      // スタンプは作成者名スナップショットを楽観的に埋め込む（サーバも作成時に同値で厳格上書きする）。
      let object: CanvasObject = baseObject;
      if (ctx.actorDisplayName) {
        if (baseObject.type === "sticky") {
          object = { ...baseObject, updatedByName: ctx.actorDisplayName };
        } else if (baseObject.type === "stamp") {
          object = { ...baseObject, createdByName: ctx.actorDisplayName };
        }
      }
      if (object.type === "table") {
        const normalizedCells = normalizeTableCells(object.cells);
        const emptyCells = normalizedCells.map((row) => row.map(() => ""));
        const emptyOp: CanvasOp = {
          type: "object.create",
          clientOpId: generateId(),
          clientCreatedAt: now,
          actorUserId: ctx.actorUserId,
          ...(ctx.actorDisplayName ? { actorDisplayName: ctx.actorDisplayName } : {}),
          object: { ...object, cells: emptyCells },
        };
        const envelopeBytes = new TextEncoder().encode(
          JSON.stringify({ type: "op.apply", op: emptyOp } satisfies ClientMessage)
        ).length;
        const fitted = fitTableCellsForBudget({
          cells: normalizedCells,
          budgetBytes: Math.min(
            CLIENT_MESSAGE_MAX_BYTES - envelopeBytes,
            TABLE_CELLS_MAX_JSON_BYTES
          ),
        });
        if (!fitted) return null;
        object = { ...object, cells: fitted.cells };
        if (fitted.clamped) ctx.onTableCellsClamped?.(object.id);
      }
      const op: CanvasOp = {
        type: "object.create",
        clientOpId: generateId(),
        clientCreatedAt: now,
        actorUserId: ctx.actorUserId,
        ...(ctx.actorDisplayName ? { actorDisplayName: ctx.actorDisplayName } : {}),
        object,
      };
      return { object, op };
    },
    [
      canCreateObjects,
      ctx.actorUserId,
      ctx.actorDisplayName,
      ctx.onTableCellsClamped,
      rejectEditWhenTrashed,
    ]
  );

  const createObject = useCallback<CanvasOpDispatcher["createObject"]>(
    (input) => {
      const built = buildCreateOp(input);
      if (!built) return null;
      dispatch(built.op, {
        undo: [makeDeleteOp(built.object.id, ctx.actorUserId, ctx.actorDisplayName)],
        redo: [built.op],
      });
      return built.object;
    },
    [buildCreateOp, ctx.actorUserId, dispatch]
  );

  const measureCreateEnvelopeBytes = useCallback<CanvasOpDispatcher["measureCreateEnvelopeBytes"]>(
    (input) => {
      const emptyTextInput = "text" in input ? { ...input, text: "" } : input;
      const built = buildCreateOp(emptyTextInput as CanvasObjectDraft);
      // 作成不可の状態では本文予算を 0 にし、呼び出し側が送信を試みないようにする。
      if (!built) return Number.MAX_SAFE_INTEGER;
      const message: ClientMessage = { type: "op.apply", op: built.op };
      return new TextEncoder().encode(JSON.stringify(message)).length;
    },
    [buildCreateOp]
  );

  const createConnector = useCallback<CanvasOpDispatcher["createConnector"]>(
    (from, to, stroke) => {
      // 自己ループは作らない。
      if (from.objectId === to.objectId) return null;
      const objects = useCanvasDocStore.getState().objects;
      const fromObj = objects[from.objectId];
      const toObj = objects[to.objectId];
      if (!fromObj || !toObj) return null;
      const fromFrame = frameFromObject(fromObj);
      const toFrame = frameFromObject(toObj);
      if (!fromFrame || !toFrame) return null;
      const bbox = connectorStoredFrame(fromFrame, from.side, toFrame, to.side);
      return createObject({
        type: "connector",
        from,
        to,
        x: bbox.x,
        y: bbox.y,
        w: bbox.w,
        h: bbox.h,
        ...(stroke ? { stroke } : {}),
      });
    },
    [createObject]
  );

  const createAttachmentPlaceholder = useCallback<
    CanvasOpDispatcher["createAttachmentPlaceholder"]
  >(
    (input, localFullSrc) => {
      const built = buildCreateOp(input);
      if (!built) return null;
      // 1) プレースホルダ src を載せた create を全参加者へ配信する（peer 側の仮表示）。
      dispatch(built.op, {
        undo: [makeDeleteOp(built.object.id, ctx.actorUserId, ctx.actorDisplayName)],
        redo: [built.op],
      });
      // 2) 作成者本人だけがフル画質を即見られるよう、送信しないローカル専用の
      //    attachment.update を重ねる（inFlightOps に残るため再構築でも維持される）。
      const overrideOp: CanvasOp = {
        type: "object.attachment.update",
        ...makeMeta(),
        objectId: built.object.id,
        src: localFullSrc,
      };
      applyOptimistic(overrideOp);
      return { object: built.object, overrideOpId: overrideOp.clientOpId };
    },
    [buildCreateOp, ctx.actorUserId, dispatch, makeMeta, applyOptimistic]
  );

  const finalizeAttachmentPlaceholder = useCallback<
    CanvasOpDispatcher["finalizeAttachmentPlaceholder"]
  >(
    (objectId, overrideOpId, publicSrc) => {
      // 表示中かどうかに関わらず、まず履歴（undo/redo 両側）の create op の src を公開 URL へ更新する。
      // アップロード完了前に Undo / 削除された場合でも、後続の Redo / Undo で未解決の
      // プレースホルダ（blob:/data:）が復活しないようにするため。
      updateAttachmentCreateHistorySrc(objectId, publicSrc);
      const current = useCanvasDocStore.getState().objects[objectId];
      // すでに削除済み（Undo / 削除済み）なら本人用上書きだけ破棄して終わる。
      if (!current) {
        removeInFlightOp(overrideOpId);
        return;
      }
      // 公開 URL を全参加者へ反映（履歴は create 側の undo=delete に集約するため積まない）。
      const op: CanvasOp = {
        type: "object.attachment.update",
        ...makeMeta(),
        objectId,
        src: publicSrc,
      };
      sendOp(op);
      // 本人用のローカル blob 上書きを破棄し、以後は全員が公開 URL を表示する。
      removeInFlightOp(overrideOpId);
    },
    [makeMeta, sendOp, removeInFlightOp]
  );

  const discardAttachmentPlaceholder = useCallback<
    CanvasOpDispatcher["discardAttachmentPlaceholder"]
  >(
    (objectId, overrideOpId) => {
      removeInFlightOp(overrideOpId);
      // Redo で解決不能なプレースホルダを復活させないよう、履歴エントリも除去する。
      removeAttachmentCreateHistoryEntry(objectId);
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current) return;
      // 仮表示オブジェクトを全参加者から削除し、壊れた状態を残さない。
      sendOp(makeDeleteOp(objectId, ctx.actorUserId, ctx.actorDisplayName));
    },
    [removeInFlightOp, ctx.actorUserId, sendOp]
  );

  const createObjects = useCallback<CanvasOpDispatcher["createObjects"]>(
    (inputs) => {
      if (rejectEditWhenTrashed()) return [];
      if (inputs.length === 0) return [];
      if (!canCreateObjects(inputs.length)) return [];
      const now = new Date().toISOString();
      const objects = useCanvasDocStore.getState().objects;
      let nextZ = computeNextZOrder(objects);
      const created: CanvasObject[] = [];
      for (const input of inputs) {
        const baseObject = clampCanvasObjectToCanvas({
          ...input,
          id: input.id ?? generateId(),
          version: 1,
          // 各反復でも永続化上限へ丸める。computeNextZOrder の初期値だけでなく、複数貼り付け /
          // Alt 複製のループ内 `nextZ += 1` が上限を突き抜けると 2 件目以降の object.create が
          // サーバ検証で reject されて楽観作成が巻き戻るため、都度クランプする。
          zOrder: clampZOrderForPersist(nextZ),
          createdAt: now,
          createdBy: ctx.actorUserId,
          updatedAt: now,
          updatedBy: ctx.actorUserId,
        } as CanvasObject);
        // 単発 createObject（buildCreateOp）と同様に、付箋は最終更新者名、スタンプは作成者名の
        // スナップショットを楽観反映する（サーバも作成時に同値で厳格上書きする）。
        // 複製元の createdByName は cloneObjectAsDraft で除去済みのため、ここで貼り付けた本人の名前になる。
        let object: CanvasObject = baseObject;
        if (ctx.actorDisplayName) {
          if (baseObject.type === "sticky") {
            object = { ...baseObject, updatedByName: ctx.actorDisplayName };
          } else if (baseObject.type === "stamp") {
            object = { ...baseObject, createdByName: ctx.actorDisplayName };
          }
        }
        if (object.type === "table") {
          const normalizedCells = normalizeTableCells(object.cells);
          const emptyCells = normalizedCells.map((row) => row.map(() => ""));
          const emptyObject = { ...object, cells: emptyCells };
          const emptyOp: CanvasOp = {
            type: "object.create",
            clientOpId: generateId(),
            clientCreatedAt: now,
            actorUserId: ctx.actorUserId,
            ...(ctx.actorDisplayName ? { actorDisplayName: ctx.actorDisplayName } : {}),
            object: emptyObject,
          };
          const envelopeBytes = new TextEncoder().encode(
            JSON.stringify({ type: "op.apply", op: emptyOp } satisfies ClientMessage)
          ).length;
          const fitted = fitTableCellsForBudget({
            cells: normalizedCells,
            budgetBytes: Math.min(
              CLIENT_MESSAGE_MAX_BYTES - envelopeBytes,
              TABLE_CELLS_MAX_JSON_BYTES
            ),
          });
          if (!fitted) continue;
          object = { ...object, cells: fitted.cells };
          if (fitted.clamped) ctx.onTableCellsClamped?.(object.id);
        }
        created.push(object);
        nextZ += 1;
      }
      // コネクタは、同じ一括作成で新規に作る端点だけを依存ヒントとしてサーバへ伝える。
      // これによりサーバは、端点の永続化がまだ完了していない短い競合だけを待てる。
      const createdEndpointIds = collectCreatedEndpointIds(created);
      const ops: CanvasOp[] = created.map((object) => {
        const endpointCreateDependencies = pickEndpointCreateDependencies(
          object,
          createdEndpointIds
        );
        return {
          type: "object.create",
          clientOpId: generateId(),
          clientCreatedAt: now,
          actorUserId: ctx.actorUserId,
          ...(ctx.actorDisplayName ? { actorDisplayName: ctx.actorDisplayName } : {}),
          object,
          ...(endpointCreateDependencies ? { endpointCreateDependencies } : {}),
        };
      });
      dispatchMany(ops, {
        undo: created.map((object) =>
          makeDeleteOp(object.id, ctx.actorUserId, ctx.actorDisplayName)
        ),
        redo: ops,
      });
      return created;
    },
    [
      canCreateObjects,
      ctx.actorUserId,
      ctx.actorDisplayName,
      ctx.onTableCellsClamped,
      dispatchMany,
      rejectEditWhenTrashed,
    ]
  );

  const transformObject = useCallback<CanvasOpDispatcher["transformObject"]>(
    (objectId, transform) => {
      if (rejectEditWhenTrashed()) return;
      const objects = useCanvasDocStore.getState().objects;
      const current = objects[objectId];
      if (!current) return;
      // ロック中はロック切替（locked のみ）以外の transform を最終防衛として一切送らない。
      // ドラッグ・リサイズ・回転は pointerdown 時の locked チェックを行うが、開始後 pointerup
      // までの間に他クライアントから locked: true が反映されたケースを救う必要がある。
      if (current.locked && !isLockOnlyTransform(transform)) return;
      // zOrder はサーバの永続化上限（±CANVAS_OBJECT_Z_ORDER_LIMIT）へ入口で丸める。
      // 旧スキーマ由来の範囲外 zOrder を持つ既存データがあるキャンバスで、「最前面へ移動」
      // （既存 max+1）等が上限超過の op を送り、楽観更新後にサーバ reject で巻き戻って
      // 「操作が失敗したように見える」のを防ぐ（MCP の採番丸めと同じ方針）。
      const effectiveTransform = clampTransformZOrderForPersist(transform);
      const inverse = pickCurrentTransform(current, effectiveTransform);
      const op: CanvasOp = {
        type: "object.transform",
        ...makeMeta(),
        objectId,
        transform: effectiveTransform,
      };
      // 位置・サイズが変わる場合は、この図形を端点とするコネクタの保存 bbox も同じ履歴に含めて
      // 更新する（描画は端点から都度計算するが、範囲選択・ミニマップ・他参加者選択枠など AABB を
      // 参照する箇所が古い bbox を使わないようにするため）。
      const movedFrame = frameAfterTransform(current, effectiveTransform);
      const connectorUpdates = movedFrame
        ? buildConnectorBboxUpdateOps(objects, new Map([[objectId, movedFrame]]), makeMeta)
        : { ops: [], undo: [] };
      if (connectorUpdates.ops.length > 0) {
        dispatchMany([op, ...connectorUpdates.ops], {
          undo: [
            { type: "object.transform", ...makeMeta(), objectId, transform: inverse },
            ...connectorUpdates.undo,
          ],
          redo: [op, ...connectorUpdates.ops],
        });
        return;
      }
      dispatch(op, {
        undo: [{ type: "object.transform", ...makeMeta(), objectId, transform: inverse }],
        redo: [op],
      });
    },
    [dispatch, dispatchMany, makeMeta, rejectEditWhenTrashed]
  );

  // 本文・セルの実描画の大きさに合わせた図形枠の自動追随（伸縮・履歴なし）。
  // transformObject と異なり Undo 履歴へ積まない: 入力のたびに走る派生的な調整で履歴を
  // 埋めると、Ctrl+Z が「枠の大きさ戻し」に消費されて本文編集の取り消しが遠くなるため。
  const autoFitObjectFrame = useCallback<CanvasOpDispatcher["autoFitObjectFrame"]>(
    (objectId, frame, options) => {
      if (rejectEditWhenTrashed()) return;
      const objects = useCanvasDocStore.getState().objects;
      const current = objects[objectId];
      if (!current || current.locked) return;
      // 当初仕様（キャンバス外にも配置可）へ戻したため、キャンバス下端での高さ打ち切りは行わない
      // （下側余白や大サイズ外周にある text / sticky でも自動高さ調整が止まらないようにする）。
      // 実描画の大きさをそのまま用いる（サーバ側も境界クランプを撤廃済みで、確定値と乖離しない）。
      const allowShrink = options?.allowShrink === true;
      const h = pickAutoFitSize(frame.h, current.h, allowShrink);
      const w = pickAutoFitSize(frame.w, current.w, allowShrink);
      if (h === null && w === null) return;
      // 幅と高さは 1 つの transform にまとめる（別々の op にすると片方だけ確定した
      // 中間状態が見え、表では一瞬はみ出しが出る）。
      const transform = {
        ...(w === null ? {} : { w }),
        ...(h === null ? {} : { h }),
      };
      const op: CanvasOp = {
        type: "object.transform",
        ...makeMeta(),
        objectId,
        transform,
      };
      // サイズが変わるため、この図形を端点とするコネクタの保存 bbox も一緒に更新する
      // （transformObject と同じ理由。範囲選択・ミニマップ等が古い bbox を参照しないようにする）。
      const movedFrame = frameAfterTransform(current, transform);
      const connectorUpdates = movedFrame
        ? buildConnectorBboxUpdateOps(objects, new Map([[objectId, movedFrame]]), makeMeta)
        : { ops: [], undo: [] };
      dispatchMany([op, ...connectorUpdates.ops]);
    },
    [dispatchMany, makeMeta, rejectEditWhenTrashed]
  );

  // 複数オブジェクトの transform を 1 回の履歴エントリにまとめる。
  // グループドラッグなど「1 ユーザー操作 = 複数 op」を 1 回の Ctrl+Z で戻せるようにする。
  const transformObjects = useCallback<CanvasOpDispatcher["transformObjects"]>(
    (updates) => {
      if (rejectEditWhenTrashed()) return;
      if (updates.length === 0) return;
      const objects = useCanvasDocStore.getState().objects;
      const ops: CanvasOp[] = [];
      const undo: CanvasOp[] = [];

      // 位置・サイズが変わった図形の確定後フレーム（コネクタ bbox 再計算用）。
      const movedFrames = new Map<string, ConnectorFrame>();
      const updatedIds = new Set<string>();

      for (const { objectId, transform } of updates) {
        const current = objects[objectId];
        if (!current) continue;
        // 個別 transformObject と同じ locked ガード。グループ内に locked が混ざっていても
        // 個別にスキップして残りは通す（=1 entry にロック解除前の状態を保持できる）。
        if (current.locked && !isLockOnlyTransform(transform)) continue;
        // 個別 transformObject と同じく、zOrder は永続化上限へ入口で丸める。
        const effectiveTransform = clampTransformZOrderForPersist(transform);
        const inverse = pickCurrentTransform(current, effectiveTransform);
        const op: CanvasOp = {
          type: "object.transform",
          ...makeMeta(),
          objectId,
          transform: effectiveTransform,
        };
        ops.push(op);
        undo.push({ type: "object.transform", ...makeMeta(), objectId, transform: inverse });
        updatedIds.add(objectId);
        const movedFrame = frameAfterTransform(current, effectiveTransform);
        if (movedFrame) movedFrames.set(objectId, movedFrame);
      }

      if (ops.length === 0) return;

      // 端点が動いたコネクタの保存 bbox も同じ履歴に含めて更新する（updates 自体に含まれる
      // コネクタは二重更新を避けるため除外する）。
      const connectorUpdates =
        movedFrames.size > 0
          ? buildConnectorBboxUpdateOps(objects, movedFrames, makeMeta, updatedIds)
          : { ops: [], undo: [] };

      dispatchMany([...ops, ...connectorUpdates.ops], {
        undo: [...undo, ...connectorUpdates.undo],
        redo: [...ops, ...connectorUpdates.ops],
      });
    },
    [dispatchMany, makeMeta, rejectEditWhenTrashed]
  );

  const measureTextUpdateMessageBytes = useCallback<
    CanvasOpDispatcher["measureTextUpdateMessageBytes"]
  >(
    (objectId, text, inlineStyles) => {
      const current = useCanvasDocStore.getState().objects[objectId];
      const revision = textStyleRevisionOf(
        current && current.type === "text" ? current.textStyleRevision : undefined
      );
      // 実際に送る op.apply と同じ形（封筒・actor 表示名・部分書式）を組み立てて実測する。
      const op: CanvasOp = {
        type: "object.text.update",
        ...makeMeta(),
        objectId,
        text,
        ...(inlineStyles === undefined
          ? {}
          : { inlineStyles, expectedTextStyleRevision: revision }),
      };
      const message: ClientMessage = { type: "op.apply", op };
      return new TextEncoder().encode(JSON.stringify(message)).length;
    },
    [makeMeta]
  );

  const updateText = useCallback<CanvasOpDispatcher["updateText"]>(
    (objectId, text, options) => {
      if (rejectEditWhenTrashed()) return;
      const current = useCanvasDocStore.getState().objects[objectId];
      // ロック中は本文更新も行わない。useObjectTextEditor 側でも編集モードを抜けて
      // 保留中の debounce timer は破棄しているが、UI イベントや blur のタイミング差で
      // この経路に到達しても契約を守れるよう dispatcher 側でも防御する。
      if (!current || !("text" in current) || current.locked) return;
      const guarded = guardTextBodyForSend(current, text, options?.inlineStyles);
      // 切り詰めのお知らせは no-op 判定より前に出す。切り詰めで結果が現在の本文と一致した
      // （＝貼り付けた内容が丸ごと入りきらなかった）場合も、利用者へ伝える必要がある。
      noticeTextBodyClamp(current, text, guarded, showToast);
      const built = buildTextUpdateOps({
        current,
        inlineStyles: guarded.inlineStyles,
        makeMeta,
        text: guarded.text,
      });
      if (built === null) return;
      dispatch(built.forward, { undo: [built.inverse], redo: [built.forward] });
    },
    [dispatch, makeMeta, rejectEditWhenTrashed, showToast]
  );

  const updateTexts = useCallback<CanvasOpDispatcher["updateTexts"]>(
    (updates) => {
      if (rejectEditWhenTrashed()) return;
      const objects = useCanvasDocStore.getState().objects;
      const ops: CanvasOp[] = [];
      const inverseOps: CanvasOp[] = [];
      let skippedByMessageBytes = 0;
      for (const update of updates) {
        const current = objects[update.objectId];
        if (!current || !("text" in current) || current.locked) continue;
        const guarded = guardTextBodyForSend(current, update.text, update.inlineStyles);
        const built = buildTextUpdateOps({
          current,
          inlineStyles: guarded.inlineStyles,
          makeMeta,
          text: guarded.text,
        });
        // 送信サイズ上限の最終防衛。この経路には編集器の fitForSend が無いため、
        // 実測をここで行う（送ってしまうと code: "limit" で拒否され、楽観適用が
        // ロールバックされたうえに履歴エントリだけが残る）。
        if (
          built !== null &&
          measureTextUpdateMessageBytes(update.objectId, guarded.text, guarded.inlineStyles) >
            TEXT_UPDATE_MESSAGE_MAX_BYTES
        ) {
          // この対象は本文を 1 文字も保存しないため、切り詰めのお知らせは出さない
          // （「入りきらない分を切り詰めて保存した」と誤解させないため）。
          skippedByMessageBytes += 1;
          continue;
        }
        noticeTextBodyClamp(current, update.text, guarded, showToast);
        if (built === null) continue;
        ops.push(built.forward);
        inverseOps.push(built.inverse);
      }
      // 件数に依らず 1 回だけ通知する（対象ごとに出すと選択件数ぶん重なる）。
      if (skippedByMessageBytes > 0) showToast("warning", textNotSentNotice().message);
      if (ops.length === 0) return;
      // 本文更新の逆 op は互いに独立（オブジェクトごとに完結）のため、収集順のままでよい。
      dispatchMany(ops, { undo: inverseOps, redo: ops });
    },
    [dispatchMany, makeMeta, measureTextUpdateMessageBytes, rejectEditWhenTrashed, showToast]
  );

  /** 表の op を組み立てる直前に、対象が編集できる表であることを確認する。 */
  const resolveEditableTable = useCallback((objectId: string): TableCanvasObject | null => {
    const current = useCanvasDocStore.getState().objects[objectId];
    // ロック中は編集器側でも編集モードを抜けるが、UI イベントの順序差でここへ到達しても
    // 契約を守れるよう dispatcher 側でも防御する。
    if (!current || current.type !== "table" || current.locked) return null;
    return current;
  }, []);

  const updateTableCell = useCallback<CanvasOpDispatcher["updateTableCell"]>(
    (objectId, row, column, text, expectedStructureRevision) => {
      if (rejectEditWhenTrashed()) return false;
      const current = resolveEditableTable(objectId);
      if (!current) return false;
      // 呼び出し側が「位置を決めた時点の revision」を渡した場合はそれを使う。現在値で組み直すと、
      // 行・列が増減した表の同じ添字（＝別のセル）を書き換えてしまう。
      const structureRevision =
        expectedStructureRevision ?? tableStructureRevisionOf(current.structureRevision);
      const previous = current.cells[row]?.[column];
      if (previous === undefined) return false;
      // 同じ本文の再送は op を増やすだけで意味が無い（履歴も汚す）。
      if (previous === text) return false;
      // 部分書式は本文と同じ op で確定させる契約のため、送信側で新しい本文へ追従させる。
      // 追従結果が空になったセルでは**空配列を明示して送る**（省略は旧クライアント互換の
      // 「既存 run を追従させる」意味になり、消したはずの書式が残ってしまう）。
      const inlineStyles = rebaseCellInlineStyles(current, row, column, previous, text);
      // 事前検証（範囲外・1 セル / 表全体の上限）は共有関数で行い、通らない op は送らない。
      const applied = applyTableCellUpdate(current, {
        row,
        column,
        text,
        structureRevision,
        ...(inlineStyles === undefined ? {} : { inlineStyles }),
      });
      if (!applied.ok) {
        showToast("warning", applied.reason);
        return false;
      }
      const forward: UpdateTableCellOp = {
        type: "object.table.cell.update",
        ...makeMeta(),
        objectId,
        row,
        column,
        text,
        structureRevision,
        ...(inlineStyles === undefined ? {} : { inlineStyles }),
      };
      // 入力中は履歴を積まない。1 回のセル滞在ぶんの差分は、編集終了時に
      // recordTableCellHistory が 1 グループとしてまとめる。
      // 送信キューが受け付けなかった場合は false を返し、呼び出し側が「保存した」と誤認しないようにする。
      return sendOp(forward);
    },
    [makeMeta, rejectEditWhenTrashed, resolveEditableTable, sendOp, showToast]
  );

  const updateTableCellRangeStyle = useCallback<CanvasOpDispatcher["updateTableCellRangeStyle"]>(
    (objectId, position, range, patch) => {
      if (rejectEditWhenTrashed()) return false;
      const current = resolveEditableTable(objectId);
      if (!current) return false;
      const text = current.cells[position.row]?.[position.column];
      if (text === undefined) return false;
      // 他参加者が本文を短くしていた場合に備え、保存済み本文の長さで切り詰める。
      const start = Math.max(0, Math.min(range.start, text.length));
      const end = Math.max(0, Math.min(range.end, text.length));
      if (end <= start) return false;
      // 触る項目が 1 つも無い patch は op を増やすだけで意味が無い。
      if (TEXT_INLINE_STYLE_KEYS.every((key) => patch[key] === undefined)) return false;
      const previousRuns = cellInlineStylesAt(current, position.row, position.column);
      // run の組み立ては共有関数に任せる（基底書式の扱いをセルの正規化と 1 か所でそろえる）。
      const nextRuns = applyTableCellInlineRangePatches({
        patches: [{ start, end, style: patch }],
        runs: previousRuns,
        text,
      });
      // 事前検証（構造の一致・範囲外・run 件数 / 保存サイズの上限）は共有関数で行い、通らない op は送らない。
      const applied = applyTableCellUpdate(current, {
        row: position.row,
        column: position.column,
        text,
        structureRevision: position.structureRevision,
        inlineStyles: nextRuns,
      });
      if (!applied.ok) {
        showToast("warning", applied.reason);
        return false;
      }
      // 同じ色を選び直した場合など、結果が変わらない操作では履歴も op も増やさない。
      if (applied.object === current) return false;
      const base = {
        objectId,
        row: position.row,
        column: position.column,
        structureRevision: position.structureRevision,
      } as const;
      // 本文は変えないが、どちらの向きでも `inlineStyles` を必ず明示する
      //（省略は旧クライアント互換の「既存 run を追従させる」意味になり、復元先が不定になる）。
      const forward: UpdateTableCellOp = {
        type: "object.table.cell.update",
        ...makeMeta(),
        ...base,
        text,
        inlineStyles: nextRuns,
      };
      const inverse: UpdateTableCellOp = {
        type: "object.table.cell.update",
        ...makeMeta(),
        ...base,
        text,
        inlineStyles: previousRuns ?? [],
      };
      // 送信キューが受け付けた場合だけ履歴が積まれる（dispatch の契約）。
      return dispatch(forward, { undo: [inverse], redo: [forward] });
    },
    [dispatch, makeMeta, rejectEditWhenTrashed, resolveEditableTable, showToast]
  );

  const discardTableOps = useCallback<CanvasOpDispatcher["discardTableOps"]>((objectId) => {
    clearTableOpQueue(objectId);
  }, []);

  const recordTableCellHistory = useCallback<CanvasOpDispatcher["recordTableCellHistory"]>(
    (objectId, row, column, baseText, finalText, baseInlineStyles) => {
      // 差分がゼロなら履歴項目を残さない（開いて閉じただけで Ctrl+Z の対象を増やさない）。
      if (baseText === finalText) return false;
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current || current.type !== "table") return false;
      // 送信キューが受け付けなかった入力は楽観適用ごと取り消されているため、いまのセルは
      // finalText と一致しない。その状態で履歴を積むと、後の Ctrl+Y が未保存の本文を復元してしまう。
      if (current.cells[row]?.[column] !== finalText) return false;
      const structureRevision = tableStructureRevisionOf(current.structureRevision);
      const base = { objectId, row, column, structureRevision } as const;
      // Undo / Redo の op は本文と部分書式を同時に運ぶ。Undo 側の run は**編集開始時点の値**を
      // 呼び出し側から受け取る。確定後の run を編集前の本文へ rebase し直す方法は、書式が付いて
      // いた文字ごと消した編集で run が空になり（空からは書式を作り直せない）、Undo が本文だけ
      // 戻して書式を失う。
      // どちらの向きでも **`inlineStyles` を必ず明示する**（run が無い側は空配列）。省略は旧
      // クライアント互換の「既存 run を追従させる」意味になり、Undo / Redo の復元先が「そのとき
      // セルに残っていた run」に左右されて決定的でなくなる。
      const finalInlineStyles = cellInlineStylesAt(current, row, column);
      const forward: UpdateTableCellOp = {
        type: "object.table.cell.update",
        ...makeMeta(),
        ...base,
        text: finalText,
        inlineStyles: finalInlineStyles ?? [],
      };
      const inverse: UpdateTableCellOp = {
        type: "object.table.cell.update",
        ...makeMeta(),
        ...base,
        text: baseText,
        inlineStyles: baseInlineStyles ?? [],
      };
      // op 自体は入力中に送信済みのため、ここでは履歴だけを積む。
      undoStack.push({ undo: [inverse], redo: [forward] });
      if (undoStack.length > HISTORY_LIMIT) undoStack.shift();
      redoStack.length = 0;
      return true;
    },
    [makeMeta]
  );

  const updateTableShape = useCallback<CanvasOpDispatcher["updateTableShape"]>(
    (objectId, change) => {
      if (rejectEditWhenTrashed()) return false;
      const current = resolveEditableTable(objectId);
      if (!current) return false;
      const structureRevision = tableStructureRevisionOf(current.structureRevision);
      const columnFractions = buildColumnFractionsForChange(current, change);
      const applied = applyTableShapeUpdate(current, {
        structureRevision,
        change,
        ...(columnFractions === undefined ? {} : { columnFractions }),
      });
      if (!applied.ok) {
        showToast("warning", applied.reason);
        return false;
      }
      const forward: UpdateTableShapeOp = {
        type: "object.table.shape.update",
        ...makeMeta(),
        objectId,
        structureRevision,
        change,
        ...(columnFractions === undefined ? {} : { columnFractions }),
      };
      // 削除の逆は「内容つきの挿入」1 op。列の増減では変更前の比率を同じ op へ載せる。
      const inverseChange = buildTableShapeInverseChange(current, change);
      const inverseFractions =
        inverseChange.kind === "insert-column" || inverseChange.kind === "delete-column"
          ? isValidTableColumnFractions(current.columnFractions, current.cells[0]?.length ?? 0)
            ? current.columnFractions
            : undefined
          : undefined;
      const inverse: UpdateTableShapeOp = {
        type: "object.table.shape.update",
        ...makeMeta(),
        objectId,
        // 逆 op は「順方向を適用した後の表」へ当てるため、その時点の revision を初期値にする。
        // 再生時には rebaseTableHistoryOp が実測値へ組み直す。
        structureRevision: structureRevision + 1,
        change: inverseChange,
        ...(inverseFractions === undefined ? {} : { columnFractions: inverseFractions }),
      };
      // 逆 op も送信サイズ上限を守れることを、組み立て時に実測で確かめる
      // （表全体のセル本文は JSON バイト数の不変条件で抑えられているが、封筒込みで検査する）。
      if (measureOpMessageBytes(inverse) > CLIENT_MESSAGE_MAX_BYTES) {
        showToast(
          "warning",
          "表の内容が大きすぎるため、この操作は取り消せない形になります。行・列を減らしてからお試しください。"
        );
        return false;
      }
      // 送信キューが受け付けた場合だけ履歴が積まれる（dispatch の契約）。
      return dispatch(forward, { undo: [inverse], redo: [forward] });
    },
    [dispatch, makeMeta, rejectEditWhenTrashed, resolveEditableTable, showToast]
  );

  const updateTableColumnWidths = useCallback<CanvasOpDispatcher["updateTableColumnWidths"]>(
    (objectId, tableIndex, widthsEm, tableCount, headerKey) => {
      if (rejectEditWhenTrashed()) return false;
      const current = useCanvasDocStore.getState().objects[objectId];
      // ロック中は表示側でもつまみを消すが、pointerup とロックが入れ違う順序でもここへ到達し得る。
      if (!current || current.locked) return false;
      if (current.type !== "text" && current.type !== "sticky") return false;
      const previous = tableColumnWidthsAt(current.tableColumnWidths, tableIndex);
      // 同じ幅の再送は op を増やすだけで意味が無く、履歴も 1 件無駄に増える。
      if (isSameTableColumnWidths(previous, widthsEm, headerKey)) return false;
      const base = { objectId, tableCount, tableIndex } as const;
      const forward: UpdateTableColumnWidthsOp = {
        type: "object.table.columns.update",
        ...makeMeta(),
        ...base,
        widthsEm,
        columnCount: widthsEm.length,
        headerKey: widthsEm.length === 0 ? "" : headerKey,
      };
      // 事前検証（対象種別・範囲・形式）は共有関数で行い、通らない op は送らない。
      if (!applyTableColumnWidthsUpdate(current, forward).ok) return false;
      // 逆 op は「変更前の同じ index の値」。未保存だった表は空配列（＝自動列幅）へ戻す。
      const inverse: UpdateTableColumnWidthsOp = {
        type: "object.table.columns.update",
        ...makeMeta(),
        ...base,
        widthsEm: previous?.widthsEm ?? [],
        columnCount: previous?.widthsEm.length ?? 0,
        headerKey: previous?.headerKey ?? "",
      };
      // 送信キューが受け付けた場合だけ履歴が積まれる（dispatch の契約）。
      return dispatch(forward, { undo: [inverse], redo: [forward] });
    },
    [dispatch, makeMeta, rejectEditWhenTrashed]
  );

  const updateTextRangeStyle = useCallback<CanvasOpDispatcher["updateTextRangeStyle"]>(
    (objectId, range, patch) => {
      if (rejectEditWhenTrashed()) return false;
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current || current.type !== "text" || current.locked) return false;
      // 選択した時点の本文と保存本文が食い違う場合は送らない（他参加者の本文更新で
      // 同じ offset が別の文字を指すようになっているため）。
      if (current.text !== range.text) {
        showToast("warning", "本文が更新されたため、範囲を選び直してください。");
        return false;
      }
      const keys = TEXT_INLINE_STYLE_KEYS.filter((key) => patch[key] !== undefined);
      if (keys.length === 0) return false;
      const forward: UpdateTextStyleOp["rangePatches"] = [
        { start: range.start, end: range.end, style: patch },
      ];
      // 事前検証（本文不一致・範囲外・run 上限）は共有関数で行い、通らない op は送らない。
      const applied = applyTextStyleUpdate(current, {
        expectedText: current.text,
        rangePatches: forward,
      });
      if (!applied.ok) {
        if (applied.code === "limit") showToast("warning", applied.reason);
        return false;
      }
      const op: CanvasOp = {
        type: "object.text.style.update",
        ...makeMeta(),
        objectId,
        expectedText: current.text,
        rangePatches: forward,
      };
      const inverse: CanvasOp = {
        type: "object.text.style.update",
        ...makeMeta(),
        objectId,
        // Undo は「この op が確定した後の本文」に対して当てる（本文は変わらないので同値）。
        expectedText: current.text,
        rangePatches: buildRangeStyleInversePatches(current.inlineStyles, range, keys),
      };
      dispatch(op, { undo: [inverse], redo: [op] });
      return true;
    },
    [dispatch, makeMeta, rejectEditWhenTrashed, showToast]
  );

  const updateObjectsStyle = useCallback<CanvasOpDispatcher["updateObjectsStyle"]>(
    (objectIds, style) => {
      if (rejectEditWhenTrashed()) return;
      const objects = useCanvasDocStore.getState().objects;
      const ops: CanvasOp[] = [];
      const inverseOps: CanvasOp[] = [];
      for (const objectId of objectIds) {
        const current = objects[objectId];
        // 不在（他参加者が削除した直後など）とロック済みは飛ばす。サーバ権威側も同契約で拒否する。
        if (!current) continue;
        if (current.locked) continue;
        // テキスト図形の文字色・背景色・文字サイズ・太字は、部分書式の消去まで含めて
        // 原子的に確定させるため専用 op（object.text.style.update）へ振り分ける。
        // 基底値が見た目上同じでも op を送る（override が残っていれば消す必要があるため）。
        if (current.type === "text") {
          const baseStyle = pickTextBaseStyle(style);
          if (Object.keys(baseStyle).length > 0) {
            const textOps = buildTextBaseStyleOps(current, baseStyle, makeMeta);
            if (textOps) {
              ops.push(textOps.forward);
              inverseOps.push(textOps.inverse);
            }
          }
          // 文字寄せは部分書式を持たないため、従来の object.style.update 経路を維持する。
          if (style.textAlign === undefined) continue;
          const alignOnly = { textAlign: style.textAlign };
          const alignUpdate = buildStyleUpdate(current, alignOnly);
          if (Object.keys(alignUpdate.effective).length === 0) continue;
          ops.push({
            type: "object.style.update",
            ...makeMeta(),
            objectId,
            style: alignUpdate.effective,
          });
          if (Object.keys(alignUpdate.inverse).length > 0) {
            inverseOps.push({
              type: "object.style.update",
              ...makeMeta(),
              objectId,
              style: alignUpdate.inverse,
            });
          }
          continue;
        }
        const { effective, inverse } = buildStyleUpdate(current, style);
        if (Object.keys(effective).length === 0) continue;
        // op と逆 op は 1 件ごとに makeMeta() を呼び、clientOpId を必ず一意にする。
        // canvas-doc-store は inFlightOps の ack / reject / スナップショット反映をすべて
        // clientOpId 単位で管理するため、複数 op で meta を使い回すと楽観状態の再構築が壊れる
        // （1 件の ack で全件が確定扱いになる、reject で無関係な op まで巻き戻る）。
        ops.push({ type: "object.style.update", ...makeMeta(), objectId, style: effective });
        if (Object.keys(inverse).length > 0) {
          inverseOps.push({
            type: "object.style.update",
            ...makeMeta(),
            objectId,
            style: inverse,
          });
        }
      }
      if (ops.length === 0) return;
      dispatchMany(ops, { undo: inverseOps, redo: ops });
    },
    [dispatchMany, makeMeta, rejectEditWhenTrashed]
  );

  const updateObjectStyle = useCallback<CanvasOpDispatcher["updateObjectStyle"]>(
    (objectId, style) => {
      // 単一対象は一括適用の 1 件版として扱う。dispatchMany は 1 件のとき dispatch と等価
      // （履歴を 1 件積んで順に sendOp する）ため、既存の右クリックメニュー経路の挙動は変わらない。
      updateObjectsStyle([objectId], style);
    },
    [updateObjectsStyle]
  );

  const updateAttachmentSrc = useCallback<CanvasOpDispatcher["updateAttachmentSrc"]>(
    (objectId, src) => {
      if (rejectEditWhenTrashed()) return;
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current) return;
      // ロック中は添付の差し替えも行わない（transformObject / updateText / updateObjectStyle と
      // 同じ最終防衛。サーバ権威側 op-applier も同契約で reject する）。
      // アップロード完了時の確定は履歴を通さない finalizeAttachmentPlaceholder が直接送るため、
      // このガードの影響を受けない。
      if (current.locked) return;
      const previousSrc = getAttachmentSrc(current);
      const op: CanvasOp = {
        type: "object.attachment.update",
        ...makeMeta(),
        objectId,
        src,
      };
      dispatch(op, {
        undo: previousSrc
          ? [{ type: "object.attachment.update", ...makeMeta(), objectId, src: previousSrc }]
          : [],
        redo: [op],
      });
    },
    [dispatch, makeMeta, rejectEditWhenTrashed]
  );

  const updateCanvasMeta = useCallback<CanvasOpDispatcher["updateCanvasMeta"]>(
    (input) => {
      if (rejectEditWhenTrashed()) return;
      const current = useCanvasDocStore.getState().meta;
      const nextTitle = input.title?.trim();
      const op: CanvasOp = {
        type: "canvas.meta.update",
        ...makeMeta(),
        ...(nextTitle ? { title: nextTitle } : {}),
        ...(input.thumbnailKey !== undefined ? { thumbnailKey: input.thumbnailKey } : {}),
        ...(input.sizePreset !== undefined ? { sizePreset: input.sizePreset } : {}),
      };
      if (op.title === undefined && op.thumbnailKey === undefined && op.sizePreset === undefined) {
        return;
      }
      dispatch(op, {
        undo: [
          {
            type: "canvas.meta.update",
            ...makeMeta(),
            ...(op.title !== undefined ? { title: current.title } : {}),
            ...(op.thumbnailKey !== undefined && current.thumbnailKey !== undefined
              ? { thumbnailKey: current.thumbnailKey }
              : {}),
            // サイズ変更の undo は変更前のプリセット（未設定なら既定=中）へ戻す。
            ...(op.sizePreset !== undefined
              ? { sizePreset: normalizeCanvasSizePreset(current.sizePreset) }
              : {}),
          },
        ],
        redo: [op],
      });
    },
    [dispatch, makeMeta, rejectEditWhenTrashed]
  );

  const deleteObjects = useCallback<CanvasOpDispatcher["deleteObjects"]>(
    (objectIds) => {
      if (rejectEditWhenTrashed()) return;
      if (objectIds.length === 0) return;
      const objects = useCanvasDocStore.getState().objects;
      // 削除対象の図形を接続先とするコネクタも巻き込んで削除する（宙ぶらりんな矢印を残さない）。
      const requested = new Set(objectIds);
      const connectorIds = collectConnectorCascadeDeleteIds(Object.values(objects), requested);
      const allIds = [...objectIds, ...connectorIds];
      const targets = allIds.map((id) => objects[id]).filter((o): o is CanvasObject => Boolean(o));
      if (targets.length === 0) return;
      // 削除も「端点図形が先・コネクタが後」の順に送る。サーバはロック中コネクタの削除を
      // 「端点が既に存在しない（＝カスケード相当）」場合だけ例外的に許可するため、選択順が
      // 「コネクタ → 端点」だとコネクタの delete が先に届いて reject されてしまう
      //（ロックしていないコネクタでは順序に依存しないが、契約を 1 つに保つため常にこの順序で送る）。
      const orderedTargets = [
        ...targets.filter((object) => object.type !== "connector"),
        ...targets.filter((object) => object.type === "connector"),
      ];
      const ops = orderedTargets.map((object) =>
        makeDeleteOp(object.id, ctx.actorUserId, ctx.actorDisplayName)
      );
      // Undo の復元（object.create）でも、サーバの connector 端点検証に通るよう端点図形を先に、
      // コネクタを後に作成する。選択順が「コネクタ → 端点」のときに Undo でコネクタ create が
      // 端点より先に送られて reject され、矢印だけ復元されない事態を防ぐ（作成順の不変条件を
      // 削除 Undo の復元経路にも適用する）。
      // さらに、同じ履歴エントリで復元される端点は依存ヒントとしてサーバへ伝え、端点の
      // 再作成が別 Lambda で処理されて完了が遅れた場合もコネクタが確定するようにする。
      const restoredEndpointIds = collectCreatedEndpointIds(orderedTargets);
      dispatchMany(ops, {
        undo: orderedTargets.map((object) =>
          makeCreateOp(
            object,
            ctx.actorUserId,
            ctx.actorDisplayName,
            pickEndpointCreateDependencies(object, restoredEndpointIds)
          )
        ),
        redo: ops,
      });
    },
    [ctx.actorUserId, ctx.actorDisplayName, dispatchMany, rejectEditWhenTrashed]
  );

  const deleteObject = useCallback<CanvasOpDispatcher["deleteObject"]>(
    (objectId) => {
      if (rejectEditWhenTrashed()) return;
      const objects = useCanvasDocStore.getState().objects;
      const current = objects[objectId];
      if (!current) return;
      // 接続先として参照しているコネクタも一緒に削除し、宙ぶらりんな矢印を残さない。
      // 1 回の履歴エントリにまとめ、Undo で図形とコネクタがそろって復元されるようにする。
      const connectorIds = collectConnectorCascadeDeleteIds(
        Object.values(objects),
        new Set([objectId])
      );
      if (connectorIds.length > 0) {
        deleteObjects([objectId, ...connectorIds]);
        return;
      }
      const op = makeDeleteOp(objectId, ctx.actorUserId, ctx.actorDisplayName);
      dispatch(op, {
        undo: [makeCreateOp(current, ctx.actorUserId, ctx.actorDisplayName)],
        redo: [op],
      });
    },
    [ctx.actorUserId, ctx.actorDisplayName, dispatch, deleteObjects, rejectEditWhenTrashed]
  );

  const commitPenStroke = useCallback<CanvasOpDispatcher["commitPenStroke"]>(
    (points, color, thickness, _boundingBox, objectId, strokePattern = "solid") => {
      if (rejectEditWhenTrashed()) return null;
      if (!canCreateObjects(1)) return null;
      const id = objectId ?? generateId();
      const now = new Date().toISOString();
      const objects = useCanvasDocStore.getState().objects;
      const nextZ = computeNextZOrder(objects);
      const clientOpId = generateId();
      // 丸めと近接点除去の閾値は画面 px 基準で決める（ステージ固定値では最大ズームで
      // 画面 8px 相当となり、拡大して描いた細部が失われる）。確定時の拡大率を使う。
      // view.zoom は getState() で都度読む（useCallback の依存へ view を加えると、
      // パン・ズームのたびにコールバックが再生成される）。
      const { zoom } = useCanvasStore.getState().view;
      const shared = {
        actorDisplayName: ctx.actorDisplayName,
        actorUserId: ctx.actorUserId,
        clientOpId,
        color,
        id,
        now,
        thickness,
        strokePattern,
        zOrder: nextZ,
      } as const;
      // 点配列に使えるバイト数は、固定の内訳ではなく「点配列を空にした実際の op」を
      // 測って逆算する。createdBy / updatedBy には長さ上限が無く、固定内訳では
      // 送信サイズガードで reject され得るため。
      // 外接矩形は正規化後の点から決まるため、封筒サイズは最悪値（最長の数値表記）で測る。
      let budget =
        PEN_STROKE_MESSAGE_MAX_BYTES -
        measurePenStrokeMessageBytes(
          buildPenStrokeCommitOp({
            ...shared,
            boundingBox: PEN_STROKE_WORST_CASE_FRAME,
            points: [],
          }).op
        );
      let built: { op: CanvasOp; stroke: PenStrokeCanvasObject } | null = null;
      // 「正規化 → 枠計算 → 最終メッセージ組み立て → 実測」を繰り返す。封筒を最悪値で
      // 測っているため通常は 1 回で収束するが、表示名が長く予算が下限クランプに掛かった
      // 場合に備えて再実測する（最終メッセージを実測しない設計では保証にならない）。
      for (let attempt = 0; attempt < PEN_STROKE_FIT_MAX_ATTEMPTS; attempt += 1) {
        // 送信サイズを予算内へ収めてから枠を計算する。丸め・間引き後の点で枠を求めないと、
        // 保存される points と bbox が食い違う（当たり判定と選択枠がずれる）。
        const normalizedPoints = normalizePenStrokePoints(points, zoom, budget);
        // clampPointToCanvas は丸めの後に通す（NaN / Infinity の 0 化を最後に効かせる）。
        const clippedPoints = normalizedPoints.map((point) => clampPointToCanvas(point));
        const candidate = buildPenStrokeCommitOp({
          ...shared,
          boundingBox: computePenStrokeFrame(clippedPoints, thickness),
          points: clippedPoints,
        });
        const messageBytes = measurePenStrokeMessageBytes(candidate.op);
        if (messageBytes <= PEN_STROKE_MESSAGE_MAX_BYTES) {
          built = candidate;
          break;
        }
        // 超過分だけ予算を削る。64 バイトの追加余裕は、点数を減らしたことで
        // 外接矩形の桁数が変わる分に充てる。
        budget = Math.max(
          PEN_STROKE_MIN_POINTS_BUDGET_BYTES,
          budget - (messageBytes - PEN_STROKE_MESSAGE_MAX_BYTES) - 64
        );
      }
      if (!built) {
        // actorDisplayName 等が異常に長く、2 点まで減らしても収まらない場合。
        // ここは applyOptimistic より前であり、まだ何も楽観適用していない。したがって
        // 合成 op.reject を配信しても inFlightOps に対象が無く、ロールバックもトーストも
        // 起きない。同ファイル既存の拒否パターン（rejectEditWhenTrashed と同じ形）で
        // トーストを出し、null を返して呼び出し側に「図形を作らない」を委ねる。
        showToast("warning", "描画データが大きすぎるため保存できませんでした。");
        return null;
      }
      const { op, stroke } = built;
      dispatch(op, {
        undo: [makeDeleteOp(stroke.id, ctx.actorUserId, ctx.actorDisplayName)],
        redo: [op],
      });
      return stroke;
    },
    [
      canCreateObjects,
      ctx.actorUserId,
      ctx.actorDisplayName,
      dispatch,
      rejectEditWhenTrashed,
      showToast,
    ]
  );

  /**
   * 履歴の 1 エントリを再生する。`counterparts` は反対方向の op 列で、表の op を現在の構造 revision へ、
   * 本文更新の op を現在の `textStyleRevision` へ組み直してよいかの判定材料に使う
   * （同じ位置の op どうしが対応する）。
   *
   * 戻り値は「送信経路がすべての op を受け付けたか」。表の送信キューが待機上限で受け付けなかった場合は
   * false を返し、呼び出し側は履歴エントリを反対側のスタックへ移さない（移すと、送られていない再生を
   * 済んだものとして扱い、Ctrl+Y で二重に適用される）。
   */
  const replayHistoryOps = useCallback(
    (ops: readonly CanvasOp[], counterparts: readonly CanvasOp[]): boolean => {
      let skippedTableOps = 0;
      let accepted = true;
      // 添字での対応付けは、両方向の op 列の長さが一致するときだけ信頼する。長さが違うエントリでは
      // 対応関係が壊れており、無関係な op を「反対側の op」として判定すると誤った組み直しが起こる。
      // 本文更新（updateText / updateTexts）と表の編集は順方向 1 件につき逆 op 1 件で常に 1:1 だが、
      // 長さがずれるエントリは実在する（変更前の src が無い添付の差し替え、逆 op が空になる style 更新）。
      // それらは本文更新 op も表 op も含まないため現状の判定結果は変わらないが、将来こうした経路へ
      // 本文更新が混ざったときに誤った対応付けで組み直さないよう、対応が取れているときだけ使う。
      const paired = ops.length === counterparts.length;
      for (const [index, op] of ops.entries()) {
        const counterpart = paired ? counterparts[index] : undefined;
        const refreshed = refreshOpForReplay({
          actorDisplayName: ctx.actorDisplayName,
          actorUserId: ctx.actorUserId,
          counterpart,
          op,
        });
        if (!isTableQueuedOp(refreshed)) {
          sendOp(refreshed);
          continue;
        }
        const table = useCanvasDocStore.getState().objects[refreshed.objectId];
        if (!table || table.type !== "table") {
          skippedTableOps += 1;
          continue;
        }
        const rebased = rebaseTableHistoryOp(refreshed, counterpart, table);
        if (rebased === null) {
          skippedTableOps += 1;
          continue;
        }
        if (!sendOp(rebased)) accepted = false;
      }
      if (skippedTableOps > 0) {
        showToast(
          "warning",
          "表が他の利用者によって変更されたため、この操作は取り消せませんでした。最新の内容をご確認ください。"
        );
      }
      return accepted;
    },
    [ctx.actorUserId, ctx.actorDisplayName, sendOp, showToast]
  );

  const undo = useCallback((): void => {
    if (rejectEditWhenTrashed()) return;
    const entry = undoStack.pop();
    if (!entry) return;
    // ロック衝突がある場合は履歴に戻して何もしない（再ロック解除後は再度 Ctrl+Z 可能）。
    // dispatcher 側（transformObject / updateText / updateObjectStyle / updateAttachmentSrc /
    // 削除ショートカット）の locked ガードを履歴経路でも迂回されないようにする。
    // 対象 op 種別は hasLockedObjectMutationConflict を参照。
    if (hasLockedObjectMutationConflict(entry.undo)) {
      undoStack.push(entry);
      return;
    }
    // 送信経路が受け付けなかった（表の待機上限）場合は、再生できていないため履歴へ戻す。
    if (!replayHistoryOps(entry.undo, entry.redo)) {
      undoStack.push(entry);
      return;
    }
    redoStack.push(entry);
  }, [replayHistoryOps, rejectEditWhenTrashed]);

  const redo = useCallback((): void => {
    if (rejectEditWhenTrashed()) return;
    const entry = redoStack.pop();
    if (!entry) return;
    if (hasLockedObjectMutationConflict(entry.redo)) {
      redoStack.push(entry);
      return;
    }
    if (!replayHistoryOps(entry.redo, entry.undo)) {
      redoStack.push(entry);
      return;
    }
    undoStack.push(entry);
  }, [replayHistoryOps, rejectEditWhenTrashed]);

  const previewTransforms = useCallback<CanvasOpDispatcher["previewTransforms"]>(
    (updates) => {
      if (rejectEditWhenTrashed()) return;
      const items = updates
        .map(({ objectId, transform }) => ({
          objectId,
          transform: pickTransientTransform(transform),
        }))
        .filter((item) => Object.keys(item.transform).length > 0);
      const single = items.length === 1 ? items[0] : undefined;
      if (single) {
        realtime.sendMessage({
          type: "op.transient",
          op: {
            type: "object.transient.transform",
            clientOpId: generateId(),
            objectId: single.objectId,
            transform: single.transform,
          },
        });
        return;
      }
      for (let index = 0; index < items.length; index += TRANSIENT_TRANSFORM_MANY_MAX_ITEMS) {
        realtime.sendMessage({
          type: "op.transient",
          op: {
            type: "object.transient.transform.many",
            clientOpId: generateId(),
            items: items.slice(index, index + TRANSIENT_TRANSFORM_MANY_MAX_ITEMS),
          },
        });
      }
    },
    [realtime, rejectEditWhenTrashed]
  );

  const previewPenAppend = useCallback<CanvasOpDispatcher["previewPenAppend"]>(
    (strokeId, points, strokePattern = "solid") => {
      if (rejectEditWhenTrashed()) return;
      if (points.length === 0) return;
      // 一時 op も座標を丸めて送る（描画中は高頻度に送るため、丸めだけで送信量が約 30% 下がる）。
      // 桁数は確定時と同じ基準（画面 0.25px 相当）でそろえる。確定時より粗く丸めると、
      // プレビューと確定後の線がずれて見える。
      const { zoom } = useCanvasStore.getState().view;
      // 件数（サーバスキーマの 512）とバイト数の両方を roundPenStrokePoints が保証する。
      // 封筒（transient のラッパと strokeId 等）は確定 op より小さいため、確定側と同じ
      // 予算から一律 2KB を引いた値を用いる。
      const rounded = roundPenStrokePoints(points, zoom, PEN_STROKE_MESSAGE_MAX_BYTES - 2048);
      if (rounded.length === 0) return;
      realtime.sendMessage({
        type: "op.transient",
        op: {
          type: "pen.stroke.append",
          clientOpId: generateId(),
          strokeId,
          strokePattern,
          points: rounded.map((p) => ({ x: p.x, y: p.y, pressure: p.pressure })),
        },
      });
    },
    [realtime, rejectEditWhenTrashed]
  );

  const previewTextCursor = useCallback<CanvasOpDispatcher["previewTextCursor"]>(
    (objectId, cursorIndex, selectionEndIndex) => {
      if (rejectEditWhenTrashed()) return;
      const index = clampCursorIndex(cursorIndex);
      // 選択終端も同じ丸めを通す。丸めた終端が先頭以下（＝選択なし相当）なら載せない。
      const endIndex =
        selectionEndIndex === undefined ? undefined : clampCursorIndex(selectionEndIndex);
      realtime.sendMessage({
        type: "op.transient",
        op: {
          type: "object.text.cursor",
          clientOpId: generateId(),
          objectId,
          cursorIndex: index,
          ...(endIndex !== undefined && endIndex > index ? { selectionEndIndex: endIndex } : {}),
        },
      });
    },
    [realtime, rejectEditWhenTrashed]
  );

  const previewTableCellCursor = useCallback<CanvasOpDispatcher["previewTableCellCursor"]>(
    (objectId, cell, cursorIndex) => {
      if (rejectEditWhenTrashed()) return;
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current || current.type !== "table") return;
      realtime.sendMessage({
        type: "op.transient",
        op: {
          type: "object.text.cursor",
          clientOpId: generateId(),
          objectId,
          // セル本文は 512 文字までのため、キャレット位置もその範囲へ丸める。
          cursorIndex: Math.max(0, Math.min(Math.floor(cursorIndex), TABLE_CELL_MAX_LENGTH)),
          tableCell: {
            row: cell.row,
            column: cell.column,
            structureRevision: tableStructureRevisionOf(current.structureRevision),
          },
        },
      });
    },
    [realtime, rejectEditWhenTrashed]
  );

  // 楽観表示用のローカルなコメントメッセージを組み立てる。投稿者 ID・表示名はサーバが
  // 認可済み値で上書きし、アバターは接続時スナップショット欠落時の検証済み候補にもなる。
  const buildLocalCommentMessage = useCallback(
    (body: string, images?: readonly string[] | undefined): CommentMessage => {
      const now = new Date().toISOString();
      return {
        id: generateId(),
        authorId: ctx.actorUserId,
        authorName: ctx.actorDisplayName ?? "",
        ...(ctx.actorAvatarImageUrl ? { authorAvatar: ctx.actorAvatarImageUrl } : {}),
        body,
        // 添付画像（アップロード済み公開 URL）。空配列は載せない（サーバスキーマと表示分岐を単純にする）。
        ...(images && images.length > 0 ? { images: [...images] } : {}),
        createdAt: now,
        updatedAt: now,
      };
    },
    [ctx.actorUserId, ctx.actorDisplayName, ctx.actorAvatarImageUrl]
  );

  const createCommentThread = useCallback<CanvasOpDispatcher["createCommentThread"]>(
    (input) => {
      // 本文が空でも画像があれば投稿できる（画像だけのコメントを許容する）。
      if (input.body.trim().length === 0 && (input.images?.length ?? 0) === 0) return null;
      const message = buildLocalCommentMessage(input.body, input.images);
      // 上限・範囲チェックは buildCreateOp で共通化しつつ、コメント配置は Undo 履歴へ積まない。
      // createObject 経由（Undo=object.delete）にすると、配置後に他者が返信したスレッドを
      // 作成者の Ctrl+Z が丸ごと削除して他者コメントを巻き込む / Redo で古い単一メッセージ状態が
      // 復活する、といった履歴破綻が起きるため。返信・編集・削除と同様に非 Undo とする。
      const built = buildCreateOp({
        type: "comment-thread",
        x: input.x,
        y: input.y,
        w: COMMENT_THREAD_NOMINAL_SIZE,
        h: COMMENT_THREAD_NOMINAL_SIZE,
        messages: [message],
      });
      if (!built) return null;
      dispatch(built.op);
      return built.object;
    },
    [buildLocalCommentMessage, buildCreateOp, dispatch]
  );

  const addCommentMessage = useCallback<CanvasOpDispatcher["addCommentMessage"]>(
    (objectId, body, images) => {
      if (rejectEditWhenTrashed()) return;
      // 本文が空でも画像があれば投稿できる（画像だけの返信を許容する）。
      if (body.trim().length === 0 && (images?.length ?? 0) === 0) return;
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current || current.type !== "comment-thread") return;
      if (current.messages.length >= COMMENT_MESSAGES_MAX) {
        showToast(
          "warning",
          `1 つのコメントスレッドに追加できる件数の上限（${COMMENT_MESSAGES_MAX}件）に達しています。`
        );
        return;
      }
      const op: CanvasOp = {
        type: "comment.message.add",
        ...makeMeta(),
        objectId,
        message: buildLocalCommentMessage(body, images),
      };
      // コメントのメッセージ追加・編集・削除はキャンバスの Undo 履歴へ積まない（本文編集に近い操作のため）。
      dispatch(op);
    },
    [buildLocalCommentMessage, dispatch, makeMeta, showToast, rejectEditWhenTrashed]
  );

  const updateCommentMessage = useCallback<CanvasOpDispatcher["updateCommentMessage"]>(
    (objectId, messageId, body) => {
      if (rejectEditWhenTrashed()) return;
      if (body.trim().length === 0) return;
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current || current.type !== "comment-thread") return;
      const target = current.messages.find((m) => m.id === messageId);
      // クライアント側ガード（最終判定はサーバ）: 本人または管理者でなければ送らない。
      if (!target || (target.authorId !== ctx.actorUserId && ctx.actorCanAdmin !== true)) return;
      if (target.body === body) return;
      const op: CanvasOp = {
        type: "comment.message.update",
        ...makeMeta(),
        objectId,
        messageId,
        body,
      };
      dispatch(op);
    },
    [ctx.actorUserId, ctx.actorCanAdmin, dispatch, makeMeta, rejectEditWhenTrashed]
  );

  const deleteCommentMessage = useCallback<CanvasOpDispatcher["deleteCommentMessage"]>(
    (objectId, messageId) => {
      if (rejectEditWhenTrashed()) return;
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current || current.type !== "comment-thread") return;
      const target = current.messages.find((m) => m.id === messageId);
      if (!target || (target.authorId !== ctx.actorUserId && ctx.actorCanAdmin !== true)) return;
      const op: CanvasOp = {
        type: "comment.message.delete",
        ...makeMeta(),
        objectId,
        messageId,
      };
      dispatch(op);
    },
    [ctx.actorUserId, ctx.actorCanAdmin, dispatch, makeMeta, rejectEditWhenTrashed]
  );

  // dispatcher の identity を安定化する（メンバは全て useCallback 済み）。
  // 毎レンダーで新しいオブジェクトを返すと、dispatcher を effect 依存に持つドラッグ系フック
  // （useResizeHandle / useRotationHandle / useObjectDrag）が provider の再レンダーのたびに
  // リスナーを再バインドし、ドラッグ中だと進行中のドラッグ状態（pointerup での確定）を失って
  // 「プレビューだけ残って確定 op が送られない＝枠線とつまみがサイズ変更へ追従しない」実障害の
  // 引き金になっていた。
  return useMemo(
    () => ({
      createObject,
      measureCreateEnvelopeBytes,
      createObjects,
      createConnector,
      createAttachmentPlaceholder,
      finalizeAttachmentPlaceholder,
      discardAttachmentPlaceholder,
      transformObject,
      autoFitObjectFrame,
      transformObjects,
      updateText,
      updateTexts,
      measureTextUpdateMessageBytes,
      updateTextRangeStyle,
      updateTableCell,
      updateTableCellRangeStyle,
      updateTableShape,
      updateTableColumnWidths,
      discardTableOps,
      recordTableCellHistory,
      updateObjectStyle,
      updateObjectsStyle,
      updateAttachmentSrc,
      updateCanvasMeta,
      deleteObject,
      deleteObjects,
      createCommentThread,
      addCommentMessage,
      updateCommentMessage,
      deleteCommentMessage,
      undo,
      redo,
      commitPenStroke,
      previewTransforms,
      previewPenAppend,
      previewTextCursor,
      previewTableCellCursor,
    }),
    [
      createObject,
      measureCreateEnvelopeBytes,
      createObjects,
      createConnector,
      createAttachmentPlaceholder,
      finalizeAttachmentPlaceholder,
      discardAttachmentPlaceholder,
      transformObject,
      autoFitObjectFrame,
      transformObjects,
      updateText,
      updateTexts,
      measureTextUpdateMessageBytes,
      updateTextRangeStyle,
      updateTableCell,
      updateTableCellRangeStyle,
      updateTableShape,
      updateTableColumnWidths,
      discardTableOps,
      recordTableCellHistory,
      updateObjectStyle,
      updateObjectsStyle,
      updateAttachmentSrc,
      updateCanvasMeta,
      deleteObject,
      deleteObjects,
      createCommentThread,
      addCommentMessage,
      updateCommentMessage,
      deleteCommentMessage,
      undo,
      redo,
      commitPenStroke,
      previewTransforms,
      previewPenAppend,
      previewTextCursor,
      previewTableCellCursor,
    ]
  );
}

// テキストキャレット / 選択終端のオフセットを、サーバ側スキーマ（0..本文長上限 の整数）へ丸める。
// 負値や NaN は 0 にする。キャレットと選択終端で同じ規則を使うため関数へ切り出している。
// 上限は本文へのオフセットとして本文長上限と一致していなければならないため、同じ定数から導く。
function clampCursorIndex(value: number): number {
  return Number.isFinite(value)
    ? Math.max(0, Math.min(TEXT_BODY_MAX_LENGTH, Math.round(value)))
    : 0;
}

/**
 * 本文長上限を超える更新を、上限内へ収める（`updateText` の最終防衛）。
 *
 * 切り詰め後の本文へ部分書式を必ず追従させる。これを省くと run の offset が本文長の外側に残り、
 * CanvasOpSchema の superRefine（isValidTextInlineStyleRanges）で再び拒否される。
 * この経路には「どこが挿入分か」の情報が無いため、末尾から削るのが最も安全である。
 */
function guardTextBodyLength(
  current: CanvasObject,
  text: string,
  inlineStyles: readonly TextInlineStyleRange[] | undefined
): {
  readonly text: string;
  readonly inlineStyles: readonly TextInlineStyleRange[] | undefined;
  /** 切り詰めた文字数（表示用のコードポイント数）。 */
  readonly droppedCount: number;
} {
  if (text.length <= TEXT_BODY_MAX_LENGTH) return { droppedCount: 0, inlineStyles, text };
  const clamped = clampTextBodyAtCaret({
    caret: text.length,
    maxLength: TEXT_BODY_MAX_LENGTH,
    text,
  });
  const nextInlineStyles =
    inlineStyles === undefined || current.type !== "text"
      ? inlineStyles
      : rebaseTextInlineStyles({
          base: textBaseStyleOf(current),
          nextText: clamped.text,
          prevText: text,
          runs: inlineStyles,
        });
  return {
    droppedCount: [...text.slice(clamped.text.length)].length,
    inlineStyles: nextInlineStyles,
    text: clamped.text,
  };
}

/**
 * 本文長の最終防衛（guardTextBodyLength）を通す。
 * `updateText`（本文編集からの送信）と `updateTexts`（選択オブジェクトの一括更新）が
 * 同じ防衛を通るようにするためのラッパ。
 *
 * 通知は行わない。`updateTexts` は送信サイズ上限で対象ごと見送ることがあり、
 * 「保存しない対象の切り詰め」を知らせると誤解を招くため、通知の可否は呼び出し側が決める
 * （`noticeTextBodyClamp`）。
 */
function guardTextBodyForSend(
  current: CanvasObject,
  text: string,
  requestedInlineStyles: readonly TextInlineStyleRange[] | undefined
): {
  readonly text: string;
  readonly inlineStyles: readonly TextInlineStyleRange[] | undefined;
  /** 切り詰めた文字数（表示用のコードポイント数）。 */
  readonly droppedCount: number;
} {
  // 最終防衛: スキーマに違反する object.text.update を送らない。編集器の適用漏れや
  // 将来の呼び出し追加があっても、文字数上限を超える本文がここから先へ出ないようにする。
  // 送信サイズ上限（バイト数）は編集器側の実測調整と updateTexts の実測が担う。
  const inlineStyles = current.type === "text" ? requestedInlineStyles : undefined;
  const guarded = guardTextBodyLength(current, text, inlineStyles);
  return {
    droppedCount: guarded.droppedCount,
    inlineStyles: guarded.inlineStyles,
    text: guarded.text,
  };
}

/** 本文長で切り詰めた更新について、警告ログとお知らせを出す（切り詰め 0 件なら何もしない）。 */
function noticeTextBodyClamp(
  current: CanvasObject,
  requestedText: string,
  guarded: { readonly text: string; readonly droppedCount: number },
  showToast: (kind: "info" | "warning", message: string) => void
): void {
  if (guarded.droppedCount <= 0) return;
  // 本文は出さず、対象と切り詰め前後の長さだけを残す。
  console.warn("[canvas] 本文長上限を超える更新を切り詰めました", {
    objectId: current.id,
    from: requestedText.length,
    to: guarded.text.length,
  });
  showToast("info", textLengthClampNotice(guarded.droppedCount, TEXT_BODY_MAX_LENGTH).message);
}

/**
 * 1 件ぶんの本文更新について、送る op と Undo 用の逆 op を作る。
 * 本文を持たない / ロック中 / 本文も run も変化なしの対象では null を返す（何も送らない）。
 * `inlineStyles` はテキスト図形のときだけ採用し、`expectedTextStyleRevision` は
 * 順方向が現在値、逆方向が「この op が確定した後の値」（現在値 + 1）になる。
 */
function buildTextUpdateOps(params: {
  readonly current: CanvasObject;
  readonly text: string;
  readonly inlineStyles: readonly TextInlineStyleRange[] | undefined;
  readonly makeMeta: () => OpMeta;
}): { readonly forward: CanvasOp; readonly inverse: CanvasOp } | null {
  const { current, makeMeta, text } = params;
  if (!("text" in current) || current.locked) return null;
  const inlineStyles = current.type === "text" ? params.inlineStyles : undefined;
  // 本文も部分書式も変わらないなら送らない（no-op 判定を run まで広げる）。
  if (current.text === text && !inlineStylesChanged(current, inlineStyles)) return null;
  const revision = textStyleRevisionOf(
    current.type === "text" ? current.textStyleRevision : undefined
  );
  const styleFields =
    inlineStyles === undefined ? {} : { inlineStyles, expectedTextStyleRevision: revision };
  // forward / inverse はそれぞれ makeMeta() を呼び、clientOpId を必ず別々にする。
  const forward: CanvasOp = {
    type: "object.text.update",
    ...makeMeta(),
    objectId: current.id,
    text,
    ...styleFields,
  };
  // Undo は「変更前の本文・run・revision」へ戻す 1 op。逆 op の編集元 revision は
  // 「この op が確定した後の revision」（現在値 + 1）になる。
  const undoStyleFields =
    inlineStyles === undefined || current.type !== "text"
      ? {}
      : { inlineStyles: current.inlineStyles ?? [], expectedTextStyleRevision: revision + 1 };
  const inverse: CanvasOp = {
    type: "object.text.update",
    ...makeMeta(),
    objectId: current.id,
    text: current.text,
    ...undoStyleFields,
  };
  return { forward, inverse };
}

/**
 * 実描画の大きさ（`measured`）を枠の 1 辺へ反映してよいかを判定する。反映しない場合は null。
 *
 * - 指定の無い辺（`undefined`）・数値でない・非正は追随しない。
 * - 1px 未満の差は測定ノイズとみなして op を送らない
 *   （複数クライアントの微小な測定差で op を打ち合わないための不感帯。実測と枠が
 *   1px 未満で一致した時点で追随が止まるため、幅と高さを同時に追随させても収束する）。
 * - 縮小は allowShrink 明示時のみ（同時編集での縮み戻し競合の防止。interface コメント参照）。
 */
function pickAutoFitSize(
  measured: number | undefined,
  currentPx: number,
  allowShrink: boolean
): number | null {
  if (measured === undefined) return null;
  if (!Number.isFinite(measured) || measured <= 0) return null;
  if (Math.abs(measured - currentPx) < 1) return null;
  if (measured < currentPx && !allowShrink) return null;
  return measured;
}

// 一時 transform op が許容するフィールド（x/y/w/h/rotation）だけを取り出す。
// zOrder / locked などの永続専用フィールドはゴーストプレビューでは扱わない。
function pickTransientTransform(
  transform: ObjectTransformPatch
): Partial<{ x: number; y: number; w: number; h: number; rotation: number }> {
  const picked: Partial<{ x: number; y: number; w: number; h: number; rotation: number }> = {};
  if (transform.x !== undefined) picked.x = transform.x;
  if (transform.y !== undefined) picked.y = transform.y;
  if (transform.w !== undefined) picked.w = transform.w;
  if (transform.h !== undefined) picked.h = transform.h;
  if (transform.rotation !== undefined) picked.rotation = transform.rotation;
  return picked;
}

// 仮表示 create の履歴エントリ（undo / redo 両側の object.create）の src を、アップロード完了後の
// 確定 src（公開 URL）に更新する。これをしないと、
//  - Undo -> Redo で redo 側がプレースホルダ付き create を復元する
//  - アップロード完了前の通常削除で undo 側に保存された blob:/data: が復元される
// といった経路で、アップロードは済んでいるのに永続的に「読み込み中」表示になる。
// このため undo / redo の両側を書き換える。
function updateAttachmentCreateHistorySrc(objectId: string, src: string): void {
  for (const stack of [undoStack, redoStack]) {
    for (let i = 0; i < stack.length; i++) {
      const entry = stack[i];
      if (!entry) continue;
      let changed = false;
      const rewriteCreateSrc = (op: CanvasOp): CanvasOp => {
        if (
          op.type === "object.create" &&
          op.object.id === objectId &&
          (op.object.type === "image" || op.object.type === "video")
        ) {
          changed = true;
          return { ...op, object: { ...op.object, src } } as CanvasOp;
        }
        return op;
      };
      const undo = entry.undo.map(rewriteCreateSrc);
      const redo = entry.redo.map(rewriteCreateSrc);
      if (changed) stack[i] = { undo, redo };
    }
  }
}

// 仮表示 create の履歴エントリを除去する。アップロード失敗時に呼び、二度と解決できない
// 壊れたプレースホルダを Undo / Redo で復活させないようにする。
// create op は履歴エントリの redo 側（新規作成）だけでなく、アップロード完了前に通常削除した
// 場合の undo 側（makeCreateOp による復元用）にも積まれるため、undo / redo の両側を対象にする。
function removeAttachmentCreateHistoryEntry(objectId: string): void {
  const hasCreate = (entry: HistoryEntry): boolean =>
    [...entry.undo, ...entry.redo].some(
      (op) => op.type === "object.create" && op.object.id === objectId
    );
  for (const stack of [undoStack, redoStack]) {
    for (let i = stack.length - 1; i >= 0; i--) {
      const entry = stack[i];
      if (entry && hasCreate(entry)) stack.splice(i, 1);
    }
  }
}

/**
 * 同じユーザー操作（一括複製 / 削除 Undo の 1 履歴エントリ）で作られる端点だけを、
 * コネクタ作成の依存ヒント（`endpointCreateDependencies`）として抽出する。
 *
 * サーバはこのヒントに列挙された端点の欠落に限り、上限付きバックオフで再読してから
 * 参照整合を判定する。op.apply は op ごとに別 Lambda で処理されるため、送信順が
 * 「端点図形 → コネクタ」でも、コネクタの検証が端点の永続化完了より先行し得るため。
 *
 * 既にサーバ確定済みの端点へ繋ぐ通常のコネクタ作成や、コネクタ単体削除の Undo では
 * 待機の必要が無いので undefined（＝ヒントを付けない）を返す。
 */
function pickEndpointCreateDependencies(
  object: CanvasObject,
  createdEndpointIds: ReadonlySet<string>
): readonly string[] | undefined {
  if (object.type !== "connector") return undefined;
  const dependencies: string[] = [];
  for (const objectId of [object.from.objectId, object.to.objectId]) {
    // 自己ループ（from === to）はサーバが常に拒否するため、重複した ID を積まない。
    if (createdEndpointIds.has(objectId) && !dependencies.includes(objectId)) {
      dependencies.push(objectId);
    }
  }
  return dependencies.length > 0 ? dependencies : undefined;
}

/**
 * 同じ操作で新規作成 / 復元される「コネクタ端点になり得るオブジェクト」の id 集合を作る。
 * 判定は domain の共有ルール（isConnectorEndpointObject）に合わせ、コネクタ自身・ペン・
 * コメントピン・セクションのように端点にできない種別を除く。これらを含めてしまうと、
 * サーバが「時間経過で正当化されない種別違反」を待機上限まで待ってから拒否することになる。
 */
function collectCreatedEndpointIds(objects: readonly CanvasObject[]): ReadonlySet<string> {
  return new Set(
    objects.filter((object) => isConnectorEndpointObject(object)).map((object) => object.id)
  );
}

function makeCreateOp(
  object: CanvasObject,
  actorUserId: string,
  actorDisplayName?: string | undefined,
  endpointCreateDependencies?: readonly string[] | undefined
): CanvasOp {
  return {
    type: "object.create",
    clientOpId: generateId(),
    clientCreatedAt: new Date().toISOString(),
    actorUserId,
    ...(actorDisplayName ? { actorDisplayName } : {}),
    object,
    ...(endpointCreateDependencies ? { endpointCreateDependencies } : {}),
  };
}

/**
 * 封筒サイズ計測用の外接矩形。実際の値は正規化後の点が決まるまで分からないため、
 * このキャンバスで取り得る最長の数値表記を埋めて上界を測る。
 * w / h は実際には非負だが、符号のぶん 1 バイト長い表記にして安全側へ寄せている。
 * このオブジェクトは計測にだけ使い、送信はしない。上限の保証そのものは
 * commitPenStroke の再実測ループが担う。
 */
const PEN_STROKE_WORST_CASE_FRAME = {
  x: -1234567.89,
  y: -1234567.89,
  w: -1234567.89,
  h: -1234567.89,
} as const;

/**
 * pen.stroke.commit の op とストロークオブジェクトを組み立てる。
 * 封筒サイズの計測・最終サイズの実測・本番送信のすべてがこの 1 関数を共有するため、
 * op の形が将来変わっても予算計算が自動的に追従する。
 */
function buildPenStrokeCommitOp(params: {
  readonly actorDisplayName: string | undefined;
  readonly actorUserId: string;
  readonly boundingBox: {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  };
  readonly clientOpId: string;
  readonly color: string;
  readonly id: string;
  readonly now: string;
  readonly points: readonly StrokePoint[];
  readonly thickness: number;
  readonly strokePattern: StrokePattern;
  readonly zOrder: number;
}): { readonly op: CanvasOp; readonly stroke: PenStrokeCanvasObject } {
  const stroke: PenStrokeCanvasObject = {
    type: "pen-stroke",
    id: params.id,
    x: params.boundingBox.x,
    y: params.boundingBox.y,
    w: params.boundingBox.w,
    h: params.boundingBox.h,
    zOrder: params.zOrder,
    version: 1,
    color: params.color,
    thickness: params.thickness,
    strokePattern: params.strokePattern,
    points: params.points,
    createdAt: params.now,
    createdBy: params.actorUserId,
    updatedAt: params.now,
    updatedBy: params.actorUserId,
  };
  const op: CanvasOp = {
    type: "pen.stroke.commit",
    clientOpId: params.clientOpId,
    clientCreatedAt: params.now,
    actorUserId: params.actorUserId,
    ...(params.actorDisplayName ? { actorDisplayName: params.actorDisplayName } : {}),
    object: stroke,
  };
  return { op, stroke };
}

/** op を op.apply メッセージとして送ったときの UTF-8 バイト数。 */
function measurePenStrokeMessageBytes(op: CanvasOp): number {
  return utf8ByteLength(JSON.stringify({ type: "op.apply", op } satisfies ClientMessage));
}

function makeDeleteOp(
  objectId: string,
  actorUserId: string,
  actorDisplayName?: string | undefined
): CanvasOp {
  return {
    type: "object.delete",
    clientOpId: generateId(),
    clientCreatedAt: new Date().toISOString(),
    actorUserId,
    ...(actorDisplayName ? { actorDisplayName } : {}),
    objectId,
  };
}

function refreshOpMeta(
  op: CanvasOp,
  actorUserId: string,
  actorDisplayName: string | undefined
): CanvasOp {
  return {
    ...op,
    clientOpId: generateId(),
    clientCreatedAt: new Date().toISOString(),
    actorUserId,
    // undo/redo の実行時点での displayName で上書きする（再生時の名前が記録時点と乖離しないように）。
    ...(actorDisplayName ? { actorDisplayName } : {}),
  } as CanvasOp;
}

/**
 * op が対象とするオブジェクトの ID。対象を持たない op（キャンバス設定の更新）では undefined。
 *
 * 未 ack op（`inFlightOps`）との突き合わせに使う。対象 ID の持ち方は op 種別で異なり、
 * 新規オブジェクトを作る op は本体を `object` として持つため、`"objectId" in op` のような
 * 判定では `object.create` / `pen.stroke.commit` を取りこぼす。網羅は switch の網羅チェックで
 * 型により担保し、CanvasOp へ新しい種別が増えたときに型エラーで気づけるようにする。
 */
function opTargetObjectId(op: CanvasOp): string | undefined {
  switch (op.type) {
    case "object.create":
    case "pen.stroke.commit":
      return op.object.id;
    case "object.delete":
    case "object.transform":
    case "object.text.update":
    case "object.text.style.update":
    case "object.table.cell.update":
    case "object.table.shape.update":
    case "object.table.columns.update":
    case "object.attachment.update":
    case "object.style.update":
    case "comment.message.add":
    case "comment.message.update":
    case "comment.message.delete":
      return op.objectId;
    case "canvas.meta.update":
      return undefined;
    default: {
      // 網羅性ガード。対象オブジェクトを持つ新 op が増えたとき、ここで型エラーになって追従漏れ
      // （未 ack ガードがすり抜け、楽観適用中の対象をサーバ確定状態として扱ってしまう）を検知する。
      const exhaustiveCheck: never = op;
      void exhaustiveCheck;
      return undefined;
    }
  }
}

/**
 * Undo / Redo の再送用に op を整える。メタ情報の差し替え（`refreshOpMeta`）に加えて、
 * `expectedTextStyleRevision` を安全な場合だけ現在値へ取り直す。
 *
 * 履歴の順方向 op は記録時点の revision を、逆 op は「その op が確定した後の revision」を
 * 焼き込んでいる。revision は op が確定するたびに 1 進むため、Redo の順方向 op をそのまま
 * 再送すると必ず古い値になり、他参加者が誰も編集していなくてもサーバに競合拒否される。
 *
 * ただし `expectedTextStyleRevision` は「古い run による他参加者の変更の上書き」を防ぐ境界
 * （canvas-op.ts の UpdateTextOp を参照）であり、無条件に最新値へ差し替えるとこの保護が失われる。
 * そこで次をすべて満たすときだけ取り直す。
 *   1. 対象オブジェクトに自分の未確定 op が残っていない。`objects` は確定状態へ未確定 op を
 *      重ねた値なので、これを満たすときだけ「いま見えている本文・run・revision は
 *      サーバ確定値そのもの」と言える。
 *   2. 反対側の op（Undo なら順方向 op、Redo なら逆 op）が同じ対象の本文更新で、run を明示している。
 *   3. その op が作ったはずの本文・run が、いま見えている（＝サーバ確定の）状態と一致する。
 * どれかを満たさない場合は記録時の値のまま送り、サーバ側の既存の競合拒否へ委ねる。
 *
 * 1 が保証するのは「見えている状態がサーバ確定値であること」であって、「反対側の op が確定した
 * こと」ではない（競合拒否された op も `inFlightOps` から取り除かれるため）。取り直しの安全性は、
 * 1 と 3 を合わせた「サーバ確定状態が、反対側の op が作る内容と一致する」という事実に依る。
 * この条件が成り立つとき、送る本文と run は互いに整合した 1 組であり、確定状態はその op を
 * 取り消した直後の状態と同一なので、古い run が別の本文へ接ぎ木されることはない。
 * 確定状態がサーバの現在値から遅れていれば、取り直した値も食い違うため従来どおり拒否される。
 */
function refreshOpForReplay(params: {
  readonly op: CanvasOp;
  /** 同じ履歴エントリの反対側にある op（同じ添字）。対応付けが信頼できなければ undefined。 */
  readonly counterpart: CanvasOp | undefined;
  readonly actorUserId: string;
  readonly actorDisplayName: string | undefined;
}): CanvasOp {
  const refreshed = refreshOpMeta(params.op, params.actorUserId, params.actorDisplayName);
  if (refreshed.type !== "object.text.update") return refreshed;
  // run を伴わない本文更新は、サーバの revision 検査自体が走らないため触らない。
  if (refreshed.inlineStyles === undefined) return refreshed;
  const counterpart = params.counterpart;
  if (
    counterpart === undefined ||
    counterpart.type !== "object.text.update" ||
    counterpart.objectId !== refreshed.objectId ||
    // run を明示しない op が作った run は履歴だけからは復元できない（既存 run を単一置換差分で
    // rebase する契約のため）。判定材料が足りないので取り直さない。
    counterpart.inlineStyles === undefined
  ) {
    return refreshed;
  }
  const state = useCanvasDocStore.getState();
  // 自分の未確定 op が残っている間は、`objects` に楽観適用ぶんが乗っているためサーバ確定値を
  // 読み取れない。このとき取り直すと、拒否された Undo の楽観適用が残った状態を根拠に、
  // 直後の Redo が偶然サーバの現在値と一致して受理され、他参加者の変更を古い本文・run で
  // 上書きできてしまう。利便性より保護を優先する。
  if (state.inFlightOps.some((pending) => opTargetObjectId(pending) === refreshed.objectId)) {
    return refreshed;
  }
  const current = state.objects[refreshed.objectId];
  if (!current || current.type !== "text") return refreshed;
  // 反対側の op が作ったはずの状態と、サーバ確定状態が食い違う＝他参加者が触っている。
  if (current.text !== counterpart.text) return refreshed;
  // inlineStylesChanged は第 2 引数が undefined のとき常に false を返すため、上の分岐で
  // undefined を除外してから渡している（本文一致だけで判定しないための run 比較）。
  if (inlineStylesChanged(current, counterpart.inlineStyles)) return refreshed;
  return {
    ...refreshed,
    expectedTextStyleRevision: textStyleRevisionOf(current.textStyleRevision),
  };
}

// transform が「locked のみで構成される＝ロック切替」かを判定する。
// ロック中でもロック切替（特に解除）の経路は通したいので、ガード判定の鍵となる。
function isLockOnlyTransform(transform: ObjectTransformPatch): boolean {
  const transformKeys = Object.keys(transform);
  return transformKeys.length > 0 && transformKeys.every((key) => key === "locked");
}

/** 送信を直列化する表向けの op か。 */
function isTableQueuedOp(op: CanvasOp): op is TableQueuedOp {
  return op.type === "object.table.cell.update" || op.type === "object.table.shape.update";
}

/** 列の増減にともなう列幅比率を、現在の表から組み立てる（自動列幅の表では undefined）。 */
function buildColumnFractionsForChange(
  table: TableCanvasObject,
  change: TableShapeChange
): readonly number[] | undefined {
  const columnCount = table.cells[0]?.length ?? 0;
  if (!isValidTableColumnFractions(table.columnFractions, columnCount)) return undefined;
  if (change.kind === "insert-column") {
    return insertTableColumnFraction(table.columnFractions, change.at, columnCount);
  }
  if (change.kind === "delete-column") {
    return deleteTableColumnFraction(table.columnFractions, change.at, columnCount);
  }
  return undefined;
}

/**
 * 行・列の増減の逆操作を組み立てる。
 *
 * 削除の逆は「内容つきの挿入」1 op で表す。複数 op へ分けると、op ごとに別 Lambda で処理される
 * ため到着順が入れ替わり、先着した cell.update が範囲外として破棄されて、復元した行が空のまま残る。
 */
function buildTableShapeInverseChange(
  table: TableCanvasObject,
  change: TableShapeChange
): TableShapeChange {
  switch (change.kind) {
    case "insert-row":
      return { kind: "delete-row", at: change.at };
    case "delete-row":
      return {
        kind: "insert-row",
        at: change.at,
        cells: [...(table.cells[change.at] ?? [])],
        // 削除する行の部分書式も載せる（載せないと Undo で書式だけが失われる）。
        ...withInverseInlineStyles(
          (table.cells[change.at] ?? []).map((_cell, columnIndex) =>
            cellInlineStylesAt(table, change.at, columnIndex)
          )
        ),
      };
    case "insert-column":
      return { kind: "delete-column", at: change.at };
    case "delete-column":
      return {
        kind: "insert-column",
        at: change.at,
        cells: table.cells.map((row) => row[change.at] ?? ""),
        ...withInverseInlineStyles(
          table.cells.map((_row, rowIndex) => cellInlineStylesAt(table, rowIndex, change.at))
        ),
      };
  }
}

/**
 * 削除の逆 op へ載せるセル部分書式。1 件も無ければキーごと落とす
 * （部分書式を持たない表の逆 op を、これまでと 1 バイトも変えないため）。
 */
function withInverseInlineStyles(
  runsPerCell: readonly (readonly TextInlineStyleRange[] | undefined)[]
): { readonly inlineStyles?: TableShapeChangeInlineStyles | undefined } {
  return runsPerCell.some((runs) => runs !== undefined) ? { inlineStyles: runsPerCell } : {};
}

/**
 * セル本文の変更へ、そのセルの部分書式を追従させる（テキスト図形の本文更新と同じ共有関数）。
 *
 * 戻り値は op の `inlineStyles` へそのまま載せる値で、次の 2 つを**区別する**。
 * - `undefined`: そのセルは元から部分書式を持たない。op へキーごと載せない
 *   （省略は旧クライアント互換の追従を意味し、run が無いセルでは結果も変わらない）。
 * - `[]`: 元は部分書式を持っていたが、追従の結果すべて消えた。**空配列を明示して送る**
 *   （省略すると、受け手は「追従せよ」と解釈して消したはずの run を残してしまう）。
 */
function rebaseCellInlineStyles(
  table: TableCanvasObject,
  row: number,
  column: number,
  prevText: string,
  nextText: string
): readonly TextInlineStyleRange[] | undefined {
  const runs = cellInlineStylesAt(table, row, column);
  if (runs === undefined) return undefined;
  // 基底書式は渡さない（表のヘッダ行は object.color と別の文字色で描かれるため。
  // 正本の理由は domain の TABLE_CELL_INLINE_STYLE_BASE にある）。
  return rebaseTextInlineStyles({ base: {}, nextText, prevText, runs });
}

/** op 1 件を op.apply メッセージとして送るときの実バイト数。逆 op の組み立て時検査に使う。 */
function measureOpMessageBytes(op: CanvasOp): number {
  const message: ClientMessage = { type: "op.apply", op };
  return new TextEncoder().encode(JSON.stringify(message)).length;
}

/** 2 つのセル列（行または列の内容）が完全に一致するか。 */
function sameCellValues(actual: readonly string[], expected: readonly string[]): boolean {
  return (
    actual.length === expected.length && actual.every((value, index) => value === expected[index])
  );
}

/**
 * 履歴から再送する表 op を、現在の構造 revision へ組み直す。
 *
 * 構造 revision は行・列の増減のたびに進むため、履歴に記録した値は再生時点では必ず古い。
 * そのまま送ると常に競合拒否になるので、**「その操作が破壊するものが、履歴どおりの内容であると
 * 実測で証明できる場合だけ」** 現在値へ組み直して送る。証明できない場合（他参加者が同じ場所を
 * 変更した、MCP の全置換が挟まった等）は送らず、理由を通知する。
 *
 * - `cell.update`: 上書き対象のセルが、対になる op（redo なら undo 側、undo なら redo 側）の
 *   本文と一致すること＝自分が書いた値がまだ残っていること。
 * - `delete-row` / `delete-column`: 消す行・列の内容が、対になる挿入 op の内容と一致すること。
 * - `insert-row` / `insert-column`: 内容を壊さないため、位置の妥当性だけを要求する
 *   （妥当性そのものは applyTableShapeUpdate が検証する）。
 */
function rebaseTableHistoryOp(
  op: TableQueuedOp,
  counterpart: CanvasOp | undefined,
  table: TableCanvasObject
): TableQueuedOp | null {
  const revision = tableStructureRevisionOf(table.structureRevision);
  if (op.type === "object.table.cell.update") {
    if (op.structureRevision === revision) return op;
    if (counterpart?.type !== "object.table.cell.update") return null;
    if (counterpart.row !== op.row || counterpart.column !== op.column) return null;
    if (table.cells[op.row]?.[op.column] !== counterpart.text) return null;
    return { ...op, structureRevision: revision };
  }
  const change = op.change;
  if (change.kind === "delete-row" || change.kind === "delete-column") {
    const expected = counterpartInsertedValues(counterpart, change, table);
    if (expected === null) return null;
    const actual =
      change.kind === "delete-row"
        ? (table.cells[change.at] ?? null)
        : table.cells.map((row) => row[change.at] ?? "");
    if (actual === null || !sameCellValues(actual, expected)) return null;
  }
  const columnFractions = resolveReplayColumnFractions(op, change, table);
  // 行の増減では columnFractions を載せてはいけないため、履歴値ごと落としてから組み直す。
  const { columnFractions: _historyFractions, ...rest } = op;
  return {
    ...rest,
    structureRevision: revision,
    ...(columnFractions === undefined ? {} : { columnFractions }),
  };
}

/** 削除を再生するときに「消してよい内容」を、対になる挿入 op から求める。 */
function counterpartInsertedValues(
  counterpart: CanvasOp | undefined,
  change: TableShapeChange,
  table: TableCanvasObject
): readonly string[] | null {
  if (counterpart?.type !== "object.table.shape.update") return null;
  const inserted = counterpart.change;
  const expectedKind = change.kind === "delete-row" ? "insert-row" : "insert-column";
  if (inserted.kind !== expectedKind || inserted.at !== change.at) return null;
  if (inserted.cells !== undefined) return inserted.cells;
  // 内容を指定せずに挿入した行・列は空だったはず。現在の寸法ぶんの空文字列と突き合わせる。
  const length =
    change.kind === "delete-row" ? (table.cells[0]?.length ?? 0) : table.cells.length;
  return new Array<string>(length).fill("");
}

/** 再生時の列幅比率を決める。長さが合う履歴値を優先し、合わなければ現在の表から組み直す。 */
function resolveReplayColumnFractions(
  op: UpdateTableShapeOp,
  change: TableShapeChange,
  table: TableCanvasObject
): readonly number[] | undefined {
  if (change.kind === "insert-row" || change.kind === "delete-row") return undefined;
  const columnCount = table.cells[0]?.length ?? 0;
  if (!isValidTableColumnFractions(table.columnFractions, columnCount)) return undefined;
  const resultCount = change.kind === "insert-column" ? columnCount + 1 : columnCount - 1;
  if (op.columnFractions !== undefined && op.columnFractions.length === resultCount) {
    return op.columnFractions;
  }
  return buildColumnFractionsForChange(table, change);
}

/**
 * 保存済み列幅が、これから送ろうとしている値と同じかを判定する。
 * 同じなら op も履歴も増やさない（ドラッグを始めて同じ位置で離した場合など）。
 */
function isSameTableColumnWidths(
  previous: TableColumnWidths | null,
  widthsEm: readonly number[],
  headerKey: string
): boolean {
  // 未保存（自動列幅）と「自動へ戻す更新」は同じ状態を指す。
  if (previous === null) return widthsEm.length === 0;
  if (widthsEm.length === 0) return false;
  if (previous.headerKey !== headerKey) return false;
  if (previous.widthsEm.length !== widthsEm.length) return false;
  return previous.widthsEm.every((width, index) => width === widthsEm[index]);
}

// undo / redo に積まれた op 集合のうち、ロック中オブジェクトに対する
// 「編集系」op が含まれるかを判定する。
// 対象は次の op 種別:
//  - object.transform: 位置・サイズ・回転・zOrder などの変形（ロック切替単独だけは許可）
//  - object.style.update: 色・フォントサイズ・塗り・線の見た目変更（常にロックで抑止）
//  - object.text.update: 本文の変更（常にロックで抑止）
//  - object.attachment.update: 画像 / 動画 / リンクカードの差し替え（常にロックで抑止）
//  - object.table.cell.update / object.table.shape.update: 表のセル本文・行列構成の変更
//    （常にロックで抑止。列挙へ加えないと、表をロックした後の Ctrl+Z がそのまま送信され、
//     サーバに拒否されて「元に戻せないうえ理由も分からない」状態になる）
//  - object.table.columns.update: 本文中の Markdown 表の列幅変更（同上）
//  - object.delete: 削除（端点削除に伴う connector のカスケードだけは例外的に許可）
// 含まれている場合、そのエントリは履歴に戻して送信スキップする。
// 対象種別はサーバ権威側のガード（op-applier の assertNotLockedObjectMutation /
// assertLockedDeleteAllowed）と同じ契約にそろえ、履歴経路の op がサーバで reject されて
// 楽観更新だけが巻き戻る状態を避ける。
function hasLockedObjectMutationConflict(ops: readonly CanvasOp[]): boolean {
  // 同じ履歴エントリ内で削除されるオブジェクト id（connector のカスケード判定に使う）。
  const deletedIds = new Set(
    ops.filter((op) => op.type === "object.delete").map((op) => op.objectId)
  );
  return ops.some((op) => {
    if (
      op.type !== "object.transform" &&
      op.type !== "object.style.update" &&
      op.type !== "object.text.update" &&
      op.type !== "object.attachment.update" &&
      op.type !== "object.table.cell.update" &&
      op.type !== "object.table.shape.update" &&
      op.type !== "object.table.columns.update" &&
      op.type !== "object.delete"
    ) {
      return false;
    }
    const current = useCanvasDocStore.getState().objects[op.objectId];
    if (current?.locked !== true) return false;
    if (op.type === "object.transform") return !isLockOnlyTransform(op.transform);
    if (op.type === "object.delete") {
      // 端点図形の削除に巻き込まれる connector の削除は、ロック中でもサーバが例外的に許可する
      // （端点を失った connector は描画も選択もできない不可視データとして残るため）。
      // 同じ履歴エントリで端点も削除される場合だけ、その例外に合わせて衝突扱いしない。
      if (current.type !== "connector") return true;
      return !deletedIds.has(current.from.objectId) && !deletedIds.has(current.to.objectId);
    }
    // style / text / attachment / 表の各更新はロック切替のような例外経路を持たないため、
    // ロック中なら常に衝突扱い。
    return true;
  });
}

// style パッチの組み立て用に readonly を外した可変版。op 型（UpdateObjectStyleOp）を正本にして
// 派生させることで、op へキーを追加したときの追従漏れを型エラーとして検出できる。
type MutableStylePatch = {
  -readonly [K in keyof UpdateObjectStyleOp["style"]]: UpdateObjectStyleOp["style"][K];
};

// op スキーマ（canvas-op-schema.ts）が style の数値へ課している受理範囲。
// 永続オブジェクトのスキーマは範囲も整数性も制約しないため、逆 op へ積む値だけはここへ丸める。
const OP_STYLE_NUMBER_MIN = 1;
const OP_STYLE_NUMBER_MAX = 512;

/**
 * 逆 op へ積む数値を op スキーマの受理範囲（1〜512 の整数）へ丸める。
 *
 * `update_canvas_object` 経由などで作られた範囲外・非整数の `fontSize` / `thickness` をそのまま
 * 逆 op へ積むと、Undo の時点で op スキーマ違反になりサーバに `op.reject` される。
 * その場合の Undo は元の値そのものではなく丸めた値へ戻るが、Undo が無反応になるより
 * 範囲内の近い値へ戻るほうが利用者にとって予測可能なため、この扱いを採用する。
 *
 * 非有限値（NaN / Infinity）はここへ到達しない。JSON は両者を数値として運べず、
 * 永続スキーマ・op スキーマの `z.number()` も NaN を拒否するため。
 */
function clampStyleNumberForOp(value: number): number {
  return Math.min(OP_STYLE_NUMBER_MAX, Math.max(OP_STYLE_NUMBER_MIN, Math.round(value)));
}

// 図形線幅は永続・op スキーマとも 1〜32 の整数に制限されるため、Undo 用の値も同じ範囲へ丸める。
function clampShapeStrokeWidthForOp(value: number): number {
  return Math.min(SHAPE_STROKE_WIDTH_MAX_PX, Math.max(OP_STYLE_NUMBER_MIN, Math.round(value)));
}

// color フィールドを持つオブジェクト種別だけを白リスト化して扱う（image / video / link-card 等は除外）。
// 表（table）は表全体の文字色を TableCanvasObject.color に持ち、選択編集パネルも「文字の色」を
// 出し、サーバの mergeObjectStyle も受理する。ここから漏れていたために op が 1 件も組み立てられず、
// 「パネルから選べるのに何も変わらない」状態になっていた。
const COLOR_BEARING_TYPES: ReadonlyArray<CanvasObject["type"]> = [
  "sticky",
  "text",
  "table",
  "pen-stroke",
];

/**
 * 1 オブジェクトぶんの「実際に効果のある style」と「Undo 用の逆 style」を組み立てる。
 *
 * 対応するフィールドを持たないオブジェクト種別では効果が無いため、style の中で実際に効果の
 * あるフィールドだけを拾う。effective が空なら、その対象へ送る op は無い。
 *
 * 「効果がある」の判定には、**指定値が現在の実効値と異なること**も含める。選択編集パネルは現在値を
 * 強調表示するため、強調されている選択肢を押し直す操作が起きやすい。同値でも op を送ると、見た目が
 * 変わらないのに履歴が 1 件増えて Undo が空振りし、さらにサーバが version / updatedAt / updatedBy を
 * 更新する副作用が出る（付箋の署名 updatedByName は本文が変わるときだけ更新されるため、style 更新
 * では書き換わらない）。
 * 現在の実効値は未設定を描画既定へ正規化した値とする。`shape.stroke` /
 * `shape.strokeWidth` も描画既定値を実効値として扱う。
 *
 * 逆 op の表現について: `object.style.update` の style は「キーを省略する＝変更しない」という
 * 表現しか持たないため、変更前が未設定（undefined）だったフィールドは、そのままでは逆 op へ
 * 積めず、Undo しても値が変更後のまま残る。そこで fill / textAlign で既に採用済みの方針に
 * そろえ、**描画上の既定値**を逆 op へ積んで Undo を成立させる。Undo 後は当該フィールドが
 * undefined ではなく既定値の明示指定になるが、描画結果は同一で、以後の編集・エクスポート・
 * サーバ検証にも差は生じない。
 *
 */
/** 送信済みの run と現在の run が実質的に違うか（本文だけ同じでも run が変われば送る必要がある）。 */
function inlineStylesChanged(
  current: CanvasObject,
  inlineStyles: readonly TextInlineStyleRange[] | undefined
): boolean {
  if (inlineStyles === undefined) return false;
  if (current.type !== "text") return false;
  const previous = current.inlineStyles ?? [];
  if (previous.length !== inlineStyles.length) return true;
  return previous.some((run, index) => {
    const next = inlineStyles[index];
    if (!next) return true;
    return (
      run.start !== next.start ||
      run.end !== next.end ||
      !isSameTextInlineStyle(run.style, next.style)
    );
  });
}

/** style パッチから、テキストの全文書式（部分書式と同名の項目）だけを取り出す。 */
function pickTextBaseStyle(style: UpdateObjectStyleOp["style"]): TextBaseStylePatch {
  const base: {
    color?: string | undefined;
    fill?: string | undefined;
    fontSize?: number | undefined;
    bold?: boolean | undefined;
  } = {};
  if (style.color !== undefined) base.color = style.color;
  if (style.fill !== undefined) base.fill = style.fill;
  if (style.fontSize !== undefined) base.fontSize = style.fontSize;
  if (style.bold !== undefined) base.bold = style.bold;
  return base;
}

/**
 * テキスト図形の全文書式変更（`object.text.style.update`）の forward / inverse op を組み立てる。
 *
 * - forward: 指定項目を基底へ反映し、同名の inline override を全 run から削除する。
 *   基底値が見た目上同じでも送る（override が残っている場合に消す必要があるため）。
 * - inverse: 変更前の基底値を `baseStyle` へ、消去前の override を区間ごとの `rangePatches` へ入れ、
 *   1 op で完全復元する。他項目の inline 書式は前後とも変更しない。
 */
function buildTextBaseStyleOps(
  current: TextCanvasObject,
  baseStyle: TextBaseStylePatch,
  makeMeta: () => OpMeta
): { readonly forward: CanvasOp; readonly inverse: CanvasOp } | null {
  const keys = TEXT_INLINE_STYLE_KEYS.filter((key) => baseStyle[key] !== undefined);
  if (keys.length === 0) return null;
  const previousBase = textBaseStyleOf(current);
  const inverseBase: {
    color?: string | undefined;
    fill?: string | undefined;
    fontSize?: number | undefined;
    bold?: boolean | undefined;
  } = {};
  // 未設定から初めて指定した場合も Undo で戻せるよう、逆 op には描画既定値を積む。
  if (keys.includes("color")) inverseBase.color = previousBase.color ?? TEXT_DEFAULT_COLOR;
  if (keys.includes("fill")) inverseBase.fill = previousBase.fill ?? TEXT_FILL_TRANSPARENT;
  if (keys.includes("fontSize")) {
    inverseBase.fontSize = clampStyleNumberForOp(previousBase.fontSize ?? DEFAULT_TEXT_FONT_SIZE);
  }
  if (keys.includes("bold")) inverseBase.bold = previousBase.bold ?? false;
  const restorePatches = buildOverrideRestorePatches(current.inlineStyles, keys);
  return {
    forward: {
      type: "object.text.style.update",
      ...makeMeta(),
      objectId: current.id,
      expectedText: current.text,
      baseStyle,
    },
    inverse: {
      type: "object.text.style.update",
      ...makeMeta(),
      objectId: current.id,
      expectedText: current.text,
      baseStyle: inverseBase,
      ...(restorePatches.length > 0 ? { rangePatches: restorePatches } : {}),
    },
  };
}

/** 指定項目について、消去前の override を区間ごとに復元する patch 列を作る。 */
function buildOverrideRestorePatches(
  runs: readonly TextInlineStyleRange[] | undefined,
  keys: readonly TextInlineStyleKey[]
): readonly TextInlineRangePatch[] {
  const patches: TextInlineRangePatch[] = [];
  for (const run of runs ?? []) {
    const style = pickInlineStyleKeys(run.style, keys);
    if (Object.keys(style).length === 0) continue;
    patches.push({ start: run.start, end: run.end, style });
  }
  return patches;
}

/**
 * 範囲書式操作の逆 patch 列を作る。
 * 「対象範囲の当該項目を消す patch」＋「変更前 override を区間ごとに戻す patch 列」の順に並べ、
 * 1 op で原子的に復元する。
 */
function buildRangeStyleInversePatches(
  runs: readonly TextInlineStyleRange[] | undefined,
  range: { readonly start: number; readonly end: number },
  keys: readonly TextInlineStyleKey[]
): readonly TextInlineRangePatch[] {
  const clearStyle: TextInlineStylePatch = Object.fromEntries(keys.map((key) => [key, null]));
  const patches: TextInlineRangePatch[] = [
    { start: range.start, end: range.end, style: clearStyle },
  ];
  for (const run of runs ?? []) {
    const start = Math.max(run.start, range.start);
    const end = Math.min(run.end, range.end);
    if (end <= start) continue;
    const style = pickInlineStyleKeys(run.style, keys);
    if (Object.keys(style).length === 0) continue;
    patches.push({ start, end, style });
  }
  return patches;
}

/** override のうち、指定した項目だけを取り出す。 */
function pickInlineStyleKeys(
  style: TextInlineStyle,
  keys: readonly TextInlineStyleKey[]
): TextInlineStylePatch {
  const picked: {
    color?: string | undefined;
    fill?: string | undefined;
    fontSize?: number | undefined;
    bold?: boolean | undefined;
  } = {};
  if (keys.includes("color") && style.color !== undefined) picked.color = style.color;
  if (keys.includes("fill") && style.fill !== undefined) picked.fill = style.fill;
  if (keys.includes("fontSize") && style.fontSize !== undefined) picked.fontSize = style.fontSize;
  if (keys.includes("bold") && style.bold !== undefined) picked.bold = style.bold;
  return picked;
}

function buildStyleUpdate(
  current: CanvasObject,
  style: UpdateObjectStyleOp["style"]
): { readonly effective: MutableStylePatch; readonly inverse: MutableStylePatch } {
  const effective: MutableStylePatch = {};
  const inverse: MutableStylePatch = {};

  if (style.color !== undefined && COLOR_BEARING_TYPES.includes(current.type)) {
    const prev = (current as { color?: string | undefined }).color;
    // 付箋・テキスト・表は未設定でも描画既定色で表示されるため、その既定色を現在の実効値とする。
    // 表の既定を補完するのは、選択編集パネルの現在値（normalizeTextColorForCompare が未設定を
    // TEXT_DEFAULT_COLOR へ寄せる）と判定をそろえるためである。補完しないと、パネルが強調表示
    // している色見本を押しただけで見た目の変わらない op と履歴が 1 件積まれる。
    const currentColor =
      current.type === "sticky"
        ? (prev ?? STICKY_DEFAULT_COLOR)
        : current.type === "text" || current.type === "table"
          ? (prev ?? TEXT_DEFAULT_COLOR)
          : prev;
    if (!isSameColor(currentColor, style.color)) {
      effective.color = style.color;
      if (prev !== undefined) {
        inverse.color = prev;
      } else if (current.type === "sticky") {
        // 付箋の描画既定（sticky-object.tsx の `object.color ?? "#fff5b3"`）へ戻す。
        inverse.color = STICKY_DEFAULT_COLOR;
      } else if (current.type === "text" || current.type === "table") {
        // テキスト・表の描画既定へ戻し、未設定からの初回変更も Undo できるようにする。
        inverse.color = TEXT_DEFAULT_COLOR;
      }
    }
  }
  // 未設定も通常ウェイトとして比較し、Undo には実効値を保存する。
  if (style.bold !== undefined && current.type === "text") {
    const currentBold = current.bold ?? false;
    if (currentBold !== style.bold) {
      effective.bold = style.bold;
      inverse.bold = currentBold;
    }
  }
  // テキストと表は、いずれも図形全体の文字サイズ（fontSize）を持つ。
  if (style.fontSize !== undefined && (current.type === "text" || current.type === "table")) {
    const currentFontSize = current.fontSize ?? DEFAULT_TEXT_FONT_SIZE;
    if (currentFontSize !== style.fontSize) {
      effective.fontSize = style.fontSize;
      // 描画既定（text-object.tsx / table-object.tsx の `object.fontSize ?? 16`）へ戻す。
      // 既定値補完のあとに丸めを適用する（既定値は範囲内の整数なので丸めは効かない）。
      inverse.fontSize = clampStyleNumberForOp(currentFontSize);
    }
  }
  if (style.fill !== undefined && current.type === "shape") {
    // 未設定 / "transparent" / "none" はいずれも「背景なし」なので、その間の指定は変化なしとみなす。
    if (normalizeShapeFillForCompare(current.fill) !== normalizeShapeFillForCompare(style.fill)) {
      effective.fill = style.fill;
      // 背景なし（fill 未設定）から色を付けた場合も Undo で背景なしへ戻せるよう、
      // 逆 op には「未設定なら transparent（=塗りなし表示）」を積む（textAlign と同方針）。
      // これをしないと、塗りなし図形へ初回で背景色を付けた操作が Undo で戻らない。
      inverse.fill = current.fill ?? SHAPE_FILL_TRANSPARENT;
    }
  }
  if (style.fill !== undefined && current.type === "text") {
    // 未設定・透明表現・hex 以外はいずれも背景なしとして同一視する。
    if (normalizeTextFillForCompare(current.fill) !== normalizeTextFillForCompare(style.fill)) {
      effective.fill = style.fill;
      inverse.fill = current.fill ?? TEXT_FILL_TRANSPARENT;
    }
  }
  if (style.fill !== undefined && current.type === "section") {
    // セクションは「背景なし」を持たず未設定 = 既定色表示のため、既定色を現在の実効値として扱う。
    const currentFill = current.fill ?? SECTION_DEFAULT_FILL;
    if (!isSameColor(currentFill, style.fill)) {
      effective.fill = style.fill;
      // fill 未設定から初めて色を付けた場合の逆 op には既定色を積んで Undo を成立させる。
      inverse.fill = currentFill;
    }
  }
  if (style.stroke !== undefined && current.type === "shape") {
    if (
      normalizeShapeStrokeForCompare(current.stroke) !==
      normalizeShapeStrokeForCompare(style.stroke)
    ) {
      effective.stroke = style.stroke;
      // stroke 未設定は描画既定色を逆 op へ積み、初回変更も Undo できるようにする。
      inverse.stroke = current.stroke ?? SHAPE_DEFAULT_STROKE;
    }
  }
  if (style.strokeWidth !== undefined && current.type === "shape") {
    const currentStrokeWidth = current.strokeWidth ?? SHAPE_DEFAULT_STROKE_WIDTH;
    if (currentStrokeWidth !== style.strokeWidth) {
      effective.strokeWidth = style.strokeWidth;
      // 旧オブジェクトの未設定値は描画既定の 2px、範囲外の既存値は op 受理範囲へ戻す。
      inverse.strokeWidth = clampShapeStrokeWidthForOp(currentStrokeWidth);
    }
  }
  if (style.stroke !== undefined && current.type === "connector") {
    if (
      normalizeConnectorStrokeForCompare(current.stroke) !==
      normalizeConnectorStrokeForCompare(style.stroke)
    ) {
      effective.stroke = style.stroke;
      // 未設定の既存コネクタは描画既定色を逆 op へ積み、初回変更も Undo できるようにする。
      inverse.stroke = current.stroke ?? CONNECTOR_DEFAULT_STROKE;
    }
  }
  if (style.strokeWidth !== undefined && current.type === "connector") {
    const currentStrokeWidth = current.strokeWidth ?? CONNECTOR_DEFAULT_STROKE_WIDTH;
    if (currentStrokeWidth !== style.strokeWidth) {
      effective.strokeWidth = style.strokeWidth;
      inverse.strokeWidth = clampShapeStrokeWidthForOp(currentStrokeWidth);
    }
  }
  // 文字寄せは text / sticky にのみ適用する。変更前が未指定なら描画既定の left を逆 op へ積み、
  // Undo でも左寄せへ戻せるようにする。
  if (style.textAlign !== undefined && (current.type === "text" || current.type === "sticky")) {
    const currentTextAlign = current.textAlign ?? "left";
    if (currentTextAlign !== style.textAlign) {
      effective.textAlign = style.textAlign;
      inverse.textAlign = currentTextAlign;
    }
  }
  // 線幅はペンストロークにのみ適用する。thickness は型上も必須フィールドなので常に値がある。
  if (style.thickness !== undefined && current.type === "pen-stroke") {
    if (current.thickness !== style.thickness) {
      effective.thickness = style.thickness;
      inverse.thickness = clampStyleNumberForOp(current.thickness);
    }
  }
  if (
    style.strokePattern !== undefined &&
    (current.type === "shape" || current.type === "pen-stroke")
  ) {
    const currentPattern = resolveStrokePattern(current.strokePattern);
    if (currentPattern !== style.strokePattern) {
      effective.strokePattern = style.strokePattern;
      // 欠落した旧データは実線が実効値なので、Undo へ明示的に積む。
      inverse.strokePattern = currentPattern;
    }
  }

  return { effective, inverse };
}

/**
 * 現在の実効色と指定色が「見た目として同じ」かを判定する。
 * 現在値を確定できない（未設定で描画既定が hex ではない）場合は、同値と断定できないので false を返す。
 *
 * 判定基準は選択編集パネルの強調表示（`resolveSelectionStyleTarget`）と同じ
 * {@link normalizeColorForCompare} にそろえる。ずれると「強調されている色見本を押しても
 * 何も起きない」または「強調されていない色見本を押しても何も起きない」という不整合になる。
 */
function isSameColor(currentValue: string | undefined, nextValue: string): boolean {
  if (currentValue === undefined) return false;
  return normalizeColorForCompare(currentValue) === normalizeColorForCompare(nextValue);
}

function pickCurrentTransform(
  object: CanvasObject,
  requested: ObjectTransformPatch
): ObjectTransformPatch {
  const inverse: ObjectTransformPatch = {};
  if (requested.x !== undefined) inverse.x = object.x;
  if (requested.y !== undefined) inverse.y = object.y;
  if (requested.w !== undefined) inverse.w = object.w;
  if (requested.h !== undefined) inverse.h = object.h;
  if (requested.rotation !== undefined) inverse.rotation = object.rotation ?? 0;
  // undo（元の zOrder へ戻す op）も永続化上限へ丸める。旧スキーマ由来の範囲外 zOrder
  // （例: 5,000,000）を持つ既存データを前面 / 背面移動したあと Ctrl+Z すると、この inverse が
  // object.transform として送られて TransformSchema の上限で reject されるため。丸めると
  // 範囲外の元値へは厳密には戻らないが、上限値へ収束する（可用性優先。作成側の採番と同方針）。
  if (requested.zOrder !== undefined) inverse.zOrder = clampZOrderForPersist(object.zOrder);
  if (requested.locked !== undefined) inverse.locked = object.locked ?? false;
  if (requested.manualHeight !== undefined && object.type === "text") {
    inverse.manualHeight = object.manualHeight ?? false;
  }
  return inverse;
}

function getAttachmentSrc(object: CanvasObject): string | undefined {
  if (object.type === "image" || object.type === "video") return object.src;
  if (object.type === "link-card") return object.imageUrl;
  return undefined;
}

/**
 * transform 適用後の図形フレーム（x/y/w/h/回転）を返す。位置・サイズ・回転のいずれも変化しない
 * （zOrder / locked のみの）transform や、コネクタ自身の場合は null を返す。
 * 回転はアンカー位置（回転後の辺中点）を変えるため、回転のみの変更でも端点コネクタの
 * 保存 bbox を再計算する必要がある。
 */
function frameAfterTransform(
  current: CanvasObject,
  transform: ObjectTransformPatch
): ConnectorFrame | null {
  if (current.type === "connector") return null;
  const geometryChanged =
    transform.x !== undefined ||
    transform.y !== undefined ||
    transform.w !== undefined ||
    transform.h !== undefined ||
    transform.rotation !== undefined;
  if (!geometryChanged) return null;
  return {
    x: transform.x ?? current.x,
    y: transform.y ?? current.y,
    w: transform.w ?? current.w,
    h: transform.h ?? current.h,
    rotation: transform.rotation ?? current.rotation ?? 0,
  };
}

/**
 * 端点が動いたコネクタの保存 bbox（x/y/w/h）を最新ルートに合わせて更新する transform op 群を返す。
 * movedFrames は「移動後フレーム」を持つ図形 id → frame。未移動の端点は objects の現在値を使う。
 * excludeIds に含まれるコネクタ id は対象外（呼び出し側で別途 transform 済みの場合に二重更新を防ぐ）。
 * bbox が変化しないコネクタは op を生成しない。
 */
function buildConnectorBboxUpdateOps(
  objects: Record<string, CanvasObject>,
  movedFrames: ReadonlyMap<string, ConnectorFrame>,
  makeMeta: () => OpMeta,
  excludeIds?: ReadonlySet<string>
): { ops: CanvasOp[]; undo: CanvasOp[] } {
  const ops: CanvasOp[] = [];
  const undo: CanvasOp[] = [];
  for (const object of Object.values(objects)) {
    if (object.type !== "connector") continue;
    if (excludeIds?.has(object.id)) continue;
    if (!movedFrames.has(object.from.objectId) && !movedFrames.has(object.to.objectId)) continue;
    const fromFrame =
      movedFrames.get(object.from.objectId) ?? frameFromObject(objects[object.from.objectId]);
    const toFrame =
      movedFrames.get(object.to.objectId) ?? frameFromObject(objects[object.to.objectId]);
    if (!fromFrame || !toFrame) continue;
    const bbox = connectorStoredFrame(fromFrame, object.from.side, toFrame, object.to.side);
    if (bbox.x === object.x && bbox.y === object.y && bbox.w === object.w && bbox.h === object.h) {
      continue;
    }
    ops.push({
      type: "object.transform",
      ...makeMeta(),
      objectId: object.id,
      transform: { x: bbox.x, y: bbox.y, w: bbox.w, h: bbox.h },
    });
    undo.push({
      type: "object.transform",
      ...makeMeta(),
      objectId: object.id,
      transform: { x: object.x, y: object.y, w: object.w, h: object.h },
    });
  }
  return { ops, undo };
}

/** オブジェクトから接続用フレーム（x/y/w/h/回転）を取り出す。存在しなければ null。 */
function frameFromObject(object: CanvasObject | undefined): ConnectorFrame | null {
  if (!object) return null;
  return { x: object.x, y: object.y, w: object.w, h: object.h, rotation: object.rotation ?? 0 };
}

/**
 * 既存オブジェクトの最大 zOrder + 1 を返す（簡易な「最前面」割り当て）。
 * 旧スキーマ由来の範囲外 zOrder が既存 max のキャンバスでも、新規作成がサーバの
 * 永続化制約（±CANVAS_OBJECT_Z_ORDER_LIMIT）で reject されないよう上限へ丸める。
 */
function computeNextZOrder(objects: Record<string, CanvasObject>): number {
  let max = 0;
  for (const obj of Object.values(objects)) {
    if (obj.zOrder > max) max = obj.zOrder;
  }
  return clampZOrderForPersist(max + 1);
}

/**
 * transform パッチの zOrder をサーバの永続化上限へ丸める（zOrder 以外は変更しない）。
 * 「最前面へ移動」（既存 max+1）/「最背面へ移動」（既存 min-1）が、旧スキーマ由来の
 * 範囲外 zOrder を持つ既存データを起点に上限超過の op を送ってしまうのを入口で防ぐ。
 */
function clampTransformZOrderForPersist(
  transform: TransformObjectOp["transform"]
): TransformObjectOp["transform"] {
  if (transform.zOrder === undefined) return transform;
  return { ...transform, zOrder: clampZOrderForPersist(transform.zOrder) };
}

// 補助型: createObject に渡す引数を 1 種類ずつ作るためのヘルパー。
export type StickyDraftInput = Omit<
  StickyCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type TextDraftInput = Omit<
  TextCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type ShapeDraftInput = Omit<
  ShapeCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type ImageDraftInput = Omit<
  ImageCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type VideoDraftInput = Omit<
  VideoCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type LinkCardDraftInput = Omit<
  LinkCardCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
